import { z } from "zod";

/**
 * The API contract.
 *
 * These schemas are the seam between this SPA and the Python Lambdas that will
 * serve it in pass 2. Three rules keep them honest:
 *
 *  1. Every response is `.parse`d at the client boundary (services/api/client.ts),
 *     so a backend that drifts fails loudly here rather than silently downstream.
 *  2. MSW handlers build their responses through these SAME schemas, so the mocks
 *     cannot drift from the types.
 *  3. `npm run contract:export` emits JSON Schema to contract/, which pass 2's
 *     pytest validates the real Lambda against. That's the strongest guarantee
 *     available across a language boundary.
 *
 * Changing a schema is changing the contract. Do it deliberately.
 */

/**
 * `football` means ASSOCIATION FOOTBALL — the top five European leagues
 * (England, Spain, Germany, Italy, France) and the Champions League. Added
 * 2026-08-28 at Ned's direction.
 *
 * ⚠️ THE LABEL IS AMBIGUOUS TO THIS PRODUCT'S OWN AUDIENCE, which is Australian
 * and already sees "AFL" in the same grid. `SPORT_LABEL` renders it "Football"
 * because that is what Ned asked for; the blurbs name the competitions outright
 * so nobody has to guess which code is meant. Do not shorten those blurbs to
 * something that leaves the word standing alone.
 *
 * ⚠️ ADDING A MEMBER HERE IS THE BREAKING DIRECTION FOR A CACHED BUNDLE. An
 * older client whose enum lacks `football` fails to PARSE a catalogue containing
 * it, and `useCatalogue` parses at the boundary — so the whole directory errors
 * rather than showing five of six models. This client must deploy before the API
 * repo's `catalogue.json` starts listing the football entries.
 */
export const sportSchema = z.enum(["afl", "nba", "nfl", "football"]);
export const modeSchema = z.enum(["sport", "fantasy"]);
export const modelIdSchema = z.enum([
  "afl-sport",
  "afl-fantasy",
  "nba-sport",
  "nba-fantasy",
  "nfl-sport",
  "nfl-fantasy",
  "football-sport",
  "football-fantasy",
]);
export const buildStatusSchema = z.enum(["live", "beta", "soon", "off-roadmap"]);
export const tierIdSchema = z.enum(["free", "premium", "pro"]);

export const capabilitySchema = z.object({
  id: z.string(),
  label: z.string(),
  /** Shown on the prompt chip. §7.1: show the price before running it. */
  estimatedCredits: z.number().int().nonnegative(),
  examplePrompt: z.string(),
});

export const modelEntrySchema = z.object({
  id: modelIdSchema,
  sport: sportSchema,
  mode: modeSchema,
  persona: z.enum(["kev", "franco"]),
  name: z.string(),
  blurb: z.string(),
  build: buildStatusSchema,
  purchasable: z.boolean(),
  priceId: z.string().optional(),
  /** The standing price, PER MONTH, and the same figure Stripe charges. Under a
      promotional coupon this is what Stripe bills once the promotion ends, so it
      stays the subscription's real price.

      RENAMED FROM `weeklyPriceAud` on 2026-08-31, when prices moved from a
      weekly headline billed monthly to plain monthly pricing. It is a WIRE
      field: the server sends it and the SPA revalidates its compiled-in seed
      against `GET /catalogue` on mount. A build expecting the old key against a
      server sending the new one shows no price at all, so kev-web and RIPK ship
      together for this change — there is no version of it that is safe to
      deploy on one side only. */
  monthlyPriceAud: z.number().positive().optional(),
  /**
   * What the customer pays while a promotion runs.
   *
   * Modelled as a discount ON TOP of monthlyPriceAud, not as a second price,
   * because that's how Stripe applies it: one $4.99 recurring price plus a
   * coupon. Storing the discounted figure as THE price would put the site at
   * odds with the invoice the moment the coupon expired.
   *
   * Terms §6 commits us to saying what the rate reverts to, so anything that
   * renders this must render monthlyPriceAud beside it.
   */
  promoPriceAud: z.number().positive().optional(),
  promoLabel: z.string().optional(),
  /* Was a literal while every code ran on one feed. Football is the first to
     need two: FPL's Defensive Contribution needs clearances and ball
     recoveries, and API-Sports carries neither, so Franco runs on Sportmonks
     while Kev runs on API-Sports. A narrow enum rather than a free string — the
     value is displayed and appears in disclosure, so a typo must fail the build
     rather than the audit. */
  dataProvider: z.enum(["api-sports", "sportmonks"]),
  sampleNote: z.string().optional(),
  /* statusNote removed 2026-08-06 (Ned). It carried roadmap timing — "the chat
     surface lands next", "targeted at the finals" — which nothing renders any
     more, and leaving it on the wire meant anyone opening devtools could still
     read the delivery plan off /api/catalogue. Dropped from the schema so it
     cannot quietly come back into the payload. */
  capabilities: z.array(capabilitySchema).default([]),
});

export const catalogueResponseSchema = z.object({
  models: z.array(modelEntrySchema),
});

export const creditBalanceSchema = z.object({
  balance: z.number().int().nonnegative(),
  monthlyAllowance: z.number().int().nonnegative(),
  topUpBalance: z.number().int().nonnegative(),
  /**
   * ABSENT OR EMPTY IS A REAL STATE, and requiring a datetime here took the
   * whole app down for one account (2026-08-08).
   *
   * `handler_me._read_balance` returns `item.get("resetAt", {}).get("S", "")` —
   * an EMPTY STRING when the BALANCE row has no resetAt, which is what a
   * half-written row looks like. GRANT-TIER.md §4 warns operators about exactly
   * that row, and a failed manual grant had produced one.
   *
   * So /me answered 200 with `resetAt: ""`, this line rejected the body, and
   * `request()` threw — by design, since a contract violation is treated as a
   * real failure. The query never got data, `me.isLoading` never cleared, and
   * RequireOnboarded rendered LOADING… forever. The server was healthy
   * throughout: 200s in 45ms, dozens of them, every one discarded here.
   *
   * The rest of the app was ALREADY built for this — `formatResetDate` takes
   * `string | undefined` and returns undefined for an empty string, and a test
   * pins that case. Only this line disagreed, and it failed the hardest way
   * available: not a missing date on one line of the account page, but every
   * guarded route dead with no error on screen.
   *
   * A display field must never be able to do that. Optional here; strict where
   * it is actually rendered.
   */
  resetAt: z.union([z.iso.datetime(), z.literal("")]).optional(),
  /**
   * Credits spent this cycle, computed SERVER-SIDE (handler_me._credits) as
   * `monthlyAllowance - balance`, clamped at zero.
   *
   * Derived there rather than here on purpose. The account page states it as a
   * fact about the account, and the server is the only party entitled to say
   * what that is — the browser subtracting two numbers it was handed produces a
   * figure nobody can stand behind. The clamp matters too: an admin grant can
   * push a balance above the tier allowance, and unclamped this renders as
   * negative consumption.
   *
   * `.optional()` for the same reason as `resetAt` directly above, and this is
   * the whole of that lesson applied on the first opportunity to repeat it: an
   * older API omits the key, and a display field must never be able to take a
   * guarded route down. Account.tsx drops the line when it is missing rather
   * than deriving a substitute.
   *
   * ── AND ABSENT NOW MEANS "WE DO NOT KNOW", NOT ONLY "OLD API" (2026-08-27) ──
   * The optionality was defensive when it was written and turned out to be the
   * feature. A CURRENT server also omits this key, deliberately, whenever it has
   * no balance to subtract from: no BALANCE row yet (every free account spends
   * its first 24h in that state — the grant is delayed on purpose) or a failed
   * read. `handler_me._read_balance` returns a placeholder 0 in both cases, and
   * `allowance - 0` is not "you have spent everything", it is "we do not know"
   * wearing the costume of a number.
   *
   * It rendered as a red 100% ring reading "Cap reached" on a free account that
   * had never asked a question. Nothing on this side needed changing to fix it,
   * because the branch this comment describes was already here — which is the
   * argument for writing display fields this way, made once and then cashed.
   */
  used: z.number().int().nonnegative().optional(),
});

export const tierLimitsSchema = z.object({
  /** Internal — never rendered as a number. See the plan's note on credits vs maxTokens. */
  maxTokensPerReply: z.number().int().positive(),
  /**
   * The daily budget, in tokens (140k/420k/940k). The live limit since
   * 2026-08-28 — `kev_security.DAILY_TOKEN_BUDGET` is what enforces it.
   *
   * `.optional()` so this bundle parses against an API that predates the field.
   * Both repos are mid-rollout by definition: whichever deploys second, the
   * other is briefly talking to a version that has never heard of half of this
   * object, and a display field must not be able to take a guarded route down.
   */
  dailyTokenBudget: z.number().int().positive().optional(),
  /**
   * RETIRED 2026-08-28 — the daily cap in DISTINCT GAMES.
   *
   * Still required, because the server still sends it for one release and this
   * field being non-optional is the whole reason it does: a cached bundle
   * running this schema would fail its parse of /me the moment the server
   * dropped it, and a failed parse there hangs every guarded route on LOADING
   * rather than erroring visibly. Make it `.optional()` in the release that
   * removes it server-side, not before, and delete it the one after.
   */
  dailySoftCap: z.number().int().positive(),
  concurrentQueries: z.number().int().positive(),
});

export const entitlementsSchema = z.object({
  tier: tierIdSchema,
  modelSubs: z.array(modelIdSchema),
  credits: creditBalanceSchema,
  limits: tierLimitsSchema,
  /**
   * What has been spent against `limits`, so the app can warn before one binds.
   *
   * The daily limit has been on the wire since this endpoint shipped and the
   * count to compare it against never was, which made it a number we could
   * display and never one we could act on.
   *
   * `.optional()` throughout, for the `resetAt` reason: an API on either side of
   * the 2026-08-28 changeover omits one of these, and a display field must not
   * be able to take a guarded route down. Absent means "cannot say", and the
   * daily half of the warning simply does not fire.
   *
   * ⚠️ `gamesToday` IS NOW ALWAYS ZERO from a current server — nothing writes the
   * fixture set any more. It is read by nothing here and kept only so the shape
   * stays parseable; delete it with `dailySoftCap`.
   */
  usage: z
    .object({
      tokensToday: z.number().int().nonnegative().optional(),
      gamesToday: z.number().int().nonnegative().optional(),
    })
    .optional(),
  /**
   * When a scheduled cancellation actually takes effect, if one is scheduled.
   *
   * ⚠️ NOTHING IS GATED ON THIS. `tier` and `modelSubs` above stay exactly as they
   * are until the subscription really ends — cancelling at period end means the
   * customer keeps everything they have paid for, and the server does not revoke
   * until Stripe's `customer.subscription.deleted` lands at term. This is a date to
   * SAY, not a permission to check. Reading it as "no longer entitled" would take
   * access away the moment someone cancels, which is the bug it exists to describe.
   *
   * `.optional()` because the server omits it entirely when nothing is winding
   * down. That is deliberate on both sides: an empty string would fail this
   * datetime parse, and a rejected body at this boundary is the `resetAt` incident
   * — a 200 the client cannot read, presenting as a permanent LOADING… with no
   * failed request to point at (see meContract.test.ts).
   */
  cancelAt: z.iso.datetime().optional(),
  /** This snapshot is a cache, never truth. A webhook can invalidate it mid-session. */
  asOf: z.iso.datetime(),
});

export const profileSchema = z.object({
  userId: z.string(),
  email: z.string(),
  displayName: z.string().optional(),
  /** §3.3 / §8C — 18+ is mandatory and gates /app. */
  dobVerified: z.boolean(),
  /**
   * Enforced server-side at login by a pre-authentication trigger, and re-checked
   * on every request. See kev-aws-setup-guide.md §7.2a. Surfaced here only so the
   * UI can show a resend prompt instead of a generic failure.
   */
  emailVerified: z.boolean(),
  /* Collected on /welcome, stored on the consent row, and echoed back here.
     Nullable, not merely optional: handler_me reads them off a consent row that
     may not exist, and Python's `.get()` puts an explicit `null` on the wire
     rather than dropping the key. A `.optional()` alone would reject that. */
  givenName: z.string().nullable().optional(),
  familyName: z.string().nullable().optional(),
  /** ISO-3166 alpha-2, upper-cased server-side. Only "AU" can ever appear:
      residence is a qualifying condition, and a non-AU answer is refused with
      403 `country_not_supported` and written nowhere. */
  country: z.string().nullable().optional(),
});

/**
 * The latest consent row, or the absence of one.
 *
 * `termsVersion` is the load-bearing field, not `acceptedTerms`. Absent means
 * never onboarded; an OLDER version means the terms changed since they agreed.
 * Both send the user to /welcome, which is what turns legal.ts's "reasonable
 * notice before it takes effect" into a mechanism rather than an intention.
 *
 * Nullable throughout for the same reason as the profile fields above: an
 * account created through Cognito's own sign-up form — reachable from the
 * "Create an account" link on the sign-in page, and bookmarkable — has no
 * consent row at all, and that is precisely the case this schema exists to
 * describe.
 */
export const consentSchema = z.object({
  acceptedTerms: z.boolean(),
  termsVersion: z.string().nullable().optional(),
  recordedAt: z.iso.datetime().nullable().optional(),
});

/**
 * Whether this account has acknowledged the pricing disclaimer, and which
 * wording of it (2026-08-10).
 *
 * Same shape and same reasoning as `consentSchema` above: `version` is the
 * load-bearing field, not the boolean. Absent means never acknowledged; an
 * OLDER version means the wording changed since they clicked. Both put the
 * overlay back in front of the chat, which is what makes a reworded disclaimer
 * enforceable against people who have already used the product.
 */
export const disclaimerSchema = z.object({
  acknowledged: z.boolean(),
  version: z.string().nullable().optional(),
  recordedAt: z.iso.datetime().nullable().optional(),
});

/** What POST /me/disclaimer echoes back. The recorded time is the SERVER's. */
export const disclaimerAckResponseSchema = z.object({
  acknowledged: z.boolean(),
  version: z.string().optional(),
  recordedAt: z.iso.datetime().optional(),
});

export const meResponseSchema = z.object({
  profile: profileSchema,
  consent: consentSchema,
  /**
   * OPTIONAL, AND THE OPTIONALITY IS THE POINT — read the `resetAt` note above
   * before tightening it.
   *
   * An API that predates the disclaimer endpoint omits this key. If the field
   * were required, `request()` would throw at the boundary on a perfectly
   * healthy 200, `me.isLoading` would never clear, and EVERY guarded route
   * would render LOADING… forever. That is not hypothetical: it is exactly what
   * a strict `resetAt` did on 2026-08-08, and it cost a day.
   *
   * So the failure direction is chosen deliberately. Absent means "this server
   * does not know about the overlay", which the UI treats as "do not show it" —
   * not "show it forever against an endpoint that will 404". The standing
   * disclaimer line under the composer is still on screen either way, so the
   * compliance floor is what it was before this shipped.
   */
  disclaimer: disclaimerSchema.optional(),
  entitlements: entitlementsSchema,
});

/**
 * The Premium season board.
 *
 * PERMISSIVE ON PURPOSE, and this is the resetAt lesson applied rather than
 * restated: the strictest plausible schema here would reject a healthy 200 and
 * `request()` treats a contract violation as a hard failure. A page of numbers
 * that renders one column short is a small problem; a page that throws is a
 * blank screen. So every field the grid can survive without is optional.
 *
 * `seedProbs` in particular: it is absent from any artifact built before
 * 2026-08-10, and the exporter omits it rather than faking a uniform 1/15 —
 * a flat distribution is a CLAIM, and it would be false.
 */
export const seasonTeamSchema = z.object({
  code: z.string(),
  name: z.string(),
  conference: z.enum(["East", "West"]),
  projectedWins: z.number().optional(),
  winP10: z.number().optional(),
  winP50: z.number().optional(),
  winP90: z.number().optional(),
  pMakePlayoffs: z.number().optional(),
  /** 15 long, one per conference finishing position. Not length-checked here:
      a 14-long array is a bad build, and failing the whole page over it helps
      nobody — SeedGrid renders what it is given. */
  seedProbs: z.array(z.number()).optional(),
  projectedRank: z.number().int().optional(),
  actualWins: z.number().optional(),
  actualRank: z.number().int().optional(),
  /**
   * What Kev published for this club, one point per day, oldest first.
   *
   * Omitted below two points, because a single observation drawn as a line is a
   * claim about movement made from no movement. Out of season this moves on
   * roster changes and model revisions rather than results — `meta.historyNote`
   * carries that sentence and the page prints it.
   */
  playoffHistory: z
    .array(
      z.object({
        d: z.string(),
        p: z.number(),
        wins: z.number().nullable().optional(),
      }),
    )
    .optional(),
});

export const seasonBoardResponseSchema = z.object({
  meta: z.object({
    ratingAsOf: z.string().nullable().optional(),
    scheduleSource: z.string().nullable().optional(),
    /** The model's own limitations text, carried through verbatim from
        season_sim. Rendered, not summarised — see the page. */
    limitations: z.string().nullable().optional(),
    actualSeason: z.number().int().nullable().optional(),
    seedProbsAvailable: z.boolean().optional(),
    historyDays: z.number().int().optional(),
    /** What moves the playoff line. Printed, not summarised — see the page. */
    historyNote: z.string().nullable().optional(),
  }),
  /** Same idiom as the teaser board: the empty case is described by the payload
      so the component needs no opinion about what time of year it is. */
  state: z.enum(["live", "offseason"]),
  note: z.string().nullable().optional(),
  teams: z.array(seasonTeamSchema),
});

/**
 * One per-game line: a season, a career, or a point on the trend.
 *
 * Same permissiveness as the season board above, for the same reason — a
 * missing column should cost a cell, never the page.
 */
export const playerStatLineSchema = z.object({
  label: z.string().nullable().optional(),
  games: z.number().int().optional(),
  points: z.number().optional(),
  rebounds: z.number().optional(),
  assists: z.number().optional(),
  threes: z.number().optional(),
  minutes: z.number().optional(),
});

/**
 * A club an average was actually earned at, with the games it covers.
 *
 * A LIST on the row, not a string, because a season is not owned by one club:
 * a player traded in February carries one average across two teams, and any
 * single code beside it would be a claim the box scores do not support.
 */
export const playerClubSchema = z.object({
  code: z.string(),
  games: z.number().int(),
});

/**
 * How a player FINISHED last season: his last N games against the rest of his
 * own season, tested against a permutation null.
 *
 * `elevated` is the only field that should ever drive a badge. The raw `lift` is
 * above the player's own average for half the league — the flag is what survives
 * reshuffling his own games, which about a sixth of the league does.
 *
 * `minutesLift` is not decoration: across the league the change in points and
 * the change in minutes correlate at 0.77, so most of a lift is a bigger role
 * rather than a better shooter, and a UI that shows the points without the
 * minutes is selling the wrong fact.
 */
export const playerLateFormSchema = z.object({
  window: z.number().int(),
  mean: z.number(),
  baseline: z.number(),
  lift: z.number(),
  /** Games in the window above the season baseline — the "consistently" half. */
  above: z.number().int(),
  p: z.number(),
  minutesLift: z.number().optional(),
  per36Lift: z.number().nullable().optional(),
  elevated: z.boolean(),
});

export const playerFormRowSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  /** Where they are NOW — the live roster feed with the off-season ledger applied. */
  team: z.string().nullable().optional(),
  asOf: z.string().nullable().optional(),
  season: playerStatLineSchema,
  career: playerStatLineSchema,
  /** Oldest first, so a sparkline reads left to right with no re-sorting here. */
  trend: z.array(playerStatLineSchema).default([]),
  /**
   * Absent when the exporter could not read the warehouse. The page must then
   * say nothing about where an average was earned rather than falling back to
   * `team` — that fallback is the misattribution bug, and a silent one.
   */
  playedFor: z.array(playerClubSchema).optional(),
  /** Absent for anyone whose season was too short to test. Points only. */
  lateForm: playerLateFormSchema.optional(),
});

/** The Premium player-form table. Averages and trends only — no priced lines. */
export const playerFormResponseSchema = z.object({
  meta: z.object({
    source: z.string().nullable().optional(),
    seasonLabel: z.string().nullable().optional(),
    players: z.number().int().optional(),
    minGames: z.number().int().optional(),
    basis: z.string().nullable().optional(),
    playedForAvailable: z.boolean().optional(),
    movedSince: z.number().int().optional(),
    splitSeason: z.number().int().optional(),
    form: z
      .object({
        window: z.number().int().optional(),
        measured: z.number().int().optional(),
        elevated: z.number().int().optional(),
        alpha: z.number().optional(),
        permutations: z.number().int().optional(),
        null: z.string().optional(),
      })
      .optional(),
    /** The model's own account of what these numbers are not. Rendered verbatim. */
    caveats: z.array(z.string()).default([]),
  }),
  players: z.array(playerFormRowSchema),
});

/** Error envelope. Every non-2xx from our API looks like this. */
export const apiErrorSchema = z.object({
  error: z.string(),
  message: z.string().optional(),
  /** 402 only — what the gate refused. */
  balance: z.number().int().optional(),
  required: z.number().int().optional(),
  resetAt: z.iso.datetime().optional(),
  /** 429 only — mirrors the Retry-After header. */
  retryAfterSec: z.number().int().optional(),
});

export const checkoutSessionResponseSchema = z.object({
  sessionId: z.string(),
  /** Opaque. The app must location.assign() this, never route to it. */
  url: z.string(),
});

/**
 * The Stripe billing portal — where a customer cancels, changes plan or gets an
 * invoice.
 *
 * A URL and nothing else, on purpose. The server resolves the Stripe customer
 * from the caller's own subscription rows, so there is no id on this wire in
 * either direction: a request cannot name a customer, and a response cannot leak
 * one. Same posture as the checkout session above — opaque, cross-origin,
 * `location.assign()` only.
 *
 * ⚠️ The portal is the ONLY way to cancel. Until 2026-08-29 there was none at
 * all, while the account page and the Terms both said there was.
 */
export const billingPortalResponseSchema = z.object({
  url: z.string(),
});

/**
 * What an upgrade would cost TODAY, straight from Stripe's upcoming invoice.
 *
 * Cents, not dollars, and deliberately: the upgrade charges immediately, so this
 * number is shown to the customer before they confirm, and it has to be the one
 * Stripe will actually take. Deriving it here from the price table would be a
 * second calculation that can disagree with the invoice — and the invoice wins.
 */
export const upgradePreviewSchema = z.object({
  amountDueCents: z.number().int(),
  currency: z.string(),
  /**
   * A move DOWN is scheduled for the end of the term rather than charged now, so
   * the server sends `amountDueCents: 0` with these two set.
   *
   * Zero on its own is not enough to render from: it is indistinguishable from a
   * free upgrade, and "Charged today $0.00" beside a Confirm button is the wrong
   * thing to say to someone who is about to keep paying for another month. Both
   * are `.optional()` because an older server sends neither.
   */
  scheduled: z.boolean().optional(),
  /** Unix seconds — Stripe's `current_period_end` on the live subscription. */
  effectiveAt: z.number().int().optional(),
  /**
   * The proration, as Stripe itemises it: a credit for the unused part of the
   * plan being left and a charge for the remainder of the one being joined.
   *
   * The descriptions are STRIPE'S, not ours — they are what appears on the
   * invoice the customer can go and read, so the two cannot disagree. Amounts are
   * signed; the credit line is negative.
   *
   * `.optional()` with a default, because a server on the other side of this
   * change sends only the total, and a display field must never be able to take a
   * paid-for route down. Absent means the dialog states the total alone, which is
   * what it did before.
   */
  lines: z
    .array(z.object({ description: z.string(), amountCents: z.number().int() }))
    .optional()
    .default([]),
  subtotalCents: z.number().int().optional(),
  /**
   * Tax, ONLY when Stripe actually calculated some.
   *
   * Absent means "not stated", which is not the same as zero and must not be
   * rendered as either. The server refuses to derive a GST component from the
   * total for the reason legal.ts gives about the ABN — inferring one would
   * assert a tax registration nobody has verified.
   */
  taxCents: z.number().int().optional(),
});

/**
 * 202 from POST /billing/upgrade. Stripe has accepted the change; entitlement
 * has NOT landed yet — it arrives on the webhook, same as a checkout. Treating
 * this as "done" is the same mistake the checkout return page exists to avoid.
 */
export const upgradeAcceptedSchema = z.object({
  status: z.string(),
});

/**
 * Poll target for the checkout return page.
 *
 * `status` is what the payment did. `fulfilled` is whether the webhook has
 * actually granted entitlement — and those are different moments. The user is
 * redirected back the instant payment succeeds, which is usually BEFORE
 * fulfilment lands. Conflating them is the classic Stripe-wiring bug.
 */
export const checkoutStatusSchema = z.object({
  sessionId: z.string(),
  status: z.enum(["pending", "paid", "cancelled"]),
  fulfilled: z.boolean(),
});

/* ── the punter's own fantasy league, on the way OUT ─────────────────────── */

/**
 * Slot names the SERVER accepts, per sport. A mirror, and it has to stay one.
 *
 * ⚠️ THIS IS A COPY OF `SLOT_KEYS` IN RIPK's `kev_league_settings.py`, AND THE
 * SERVER IS THE AUTHORITY. It is an allowlist over there for a stated reason:
 * a slot name becomes a heading inside Franco's system prompt, so "any short
 * uppercase word" would put arbitrary client text into the prompt. Sending a
 * name it does not know is a 400 for the whole turn, not a dropped field — so
 * the same list lives here, and `leagueSettingsSchema` below is built from it,
 * which is what makes an unsendable slot name a TYPE ERROR in this app rather
 * than a rejection in production.
 *
 * `afl` is absent because the server has no allowlist for it either: the three
 * sports here are `SPORTS` in that module, and a settings object aimed at any
 * other model is refused with `league_settings_sport_mismatch`.
 */
export const LEAGUE_SLOT_KEYS = {
  nba: ["PG", "SG", "SF", "PF", "C", "G", "F", "GF", "FC", "UTIL"],
  nfl: [
    "QB", "RB", "WR", "TE", "FLEX", "SUPERFLEX", "OP",
    "K", "PK", "DST", "DEF", "IDP", "DL", "LB", "DB",
  ],
  football: ["GK", "DEF", "MID", "FWD"],
} as const;

/**
 * The whole-object ceiling, in bytes of compact UTF-8 JSON. The server's number.
 *
 * ⚠️ NOT AN ARBITRARY LIMIT, AND NOT ONE TO RAISE LOCALLY. This object re-sends
 * on EVERY turn (see chat.ts), inside a request body the edge WAF caps — on
 * 2026-09-05 `SizeRestrictions_BODY` killed every chat after about five turns
 * and the SPA showed it to users as a lapsed subscription. The server refuses
 * anything over this before it looks at a single field.
 */
export const MAX_LEAGUE_SETTINGS_BYTES = 1024;

/** The size the server measures: compact separators, no ASCII escaping. */
export function leagueSettingsBytes(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}

function leagueSlots<T extends readonly [string, ...string[]]>(keys: T) {
  return (
    z
      .partialRecord(z.enum(keys), z.int().min(0).max(10))
      /* `slots must not be empty` on the server. Dropped from the JSON Schema
         export (zod cannot represent a refinement), which is why `minProperties`
         is restated in `.meta` below — the Python side validates against the
         exported file, and a constraint only this client enforces is one the
         contract does not carry. */
      .refine((s) => Object.keys(s).length > 0, "at least one starting slot")
      .meta({ minProperties: 1, maxProperties: 16 })
  );
}

/* Every field except `slots`, which is the one that differs per sport. */
const leagueCommon = {
  /** A scoring format id from `features/fantasy/rules.ts`. Server cap: 40. */
  scoring: z.string().min(1).max(40),
  teams: z.int().min(2).max(32),
  /** Always sent, never omitted — the server defaults it to false when absent,
      and a default that agrees with us by luck is one that can stop agreeing. */
  superflex: z.boolean(),
  bench: z.int().min(0).max(20).optional(),
  squadSize: z.int().min(0).max(40).optional(),
  name: z.string().max(60).optional(),
};

/**
 * One reader's saved league, in the shape RIPK's `kev_league_settings.validate`
 * accepts — and, deliberately, in no larger shape than that.
 *
 * ⚠️ THE ONLY REQUEST SHAPE IN THIS FILE, and it is here rather than in the
 * feature for one reason: everything the server can refuse belongs at the
 * boundary that also EXPORTS the contract. The builder in
 * `features/fantasy/league.ts` returns nothing it has not first parsed through
 * this schema, so a payload the server would 400 cannot leave this app — an
 * out-of-bounds count, an unknown slot name, an over-long league name and an
 * oversized object are each a parse failure here, before any fetch.
 *
 * A DISCRIMINATED UNION, NOT ONE OBJECT WITH A SPORT FIELD. The slot allowlist
 * is per sport, so `{ sport: "nfl", slots: { GK: 1 } }` has to be unconstructible
 * rather than merely unlikely: this way TypeScript refuses it at the keyboard and
 * the parse refuses it at the boundary. The server's own answer to that payload
 * is a 400 on the whole turn.
 */
export const leagueSettingsSchema = z
  .discriminatedUnion("sport", [
    z.object({ sport: z.literal("nba"), slots: leagueSlots(LEAGUE_SLOT_KEYS.nba), ...leagueCommon }),
    z.object({
      sport: z.literal("football"),
      slots: leagueSlots(LEAGUE_SLOT_KEYS.football),
      ...leagueCommon,
    }),
    z.object({ sport: z.literal("nfl"), slots: leagueSlots(LEAGUE_SLOT_KEYS.nfl), ...leagueCommon }),
  ])
  .refine(
    (v) => leagueSettingsBytes(v) <= MAX_LEAGUE_SETTINGS_BYTES,
    `leagueSettings must serialise to at most ${MAX_LEAGUE_SETTINGS_BYTES} bytes`,
  );

export type LeagueSettings = z.infer<typeof leagueSettingsSchema>;
/** The sports the server holds a slot allowlist for. Not every `Sport`. */
export type LeagueSport = LeagueSettings["sport"];

/* ── the same league, SAVED TO THE ACCOUNT ───────────────────────────────── */

/**
 * The codes `/fantasy/league/{sport}` holds. `kev_league_store.SPORTS`, mirrored.
 *
 * ⚠️ THE NBA IS ABSENT AND THAT IS THE DESIGN, NOT A GAP. A basketball roster
 * already has a home — `/fantasy/teams`, written by Franco's own tool after it
 * has resolved every name against the player dataset. A second NBA squad in this
 * store would be two different answers to "my team", one checked and one not,
 * with nothing on screen to say which you were reading.
 */
export const SAVED_LEAGUE_SPORTS = ["football", "nfl"] as const;
export type SavedLeagueSport = (typeof SAVED_LEAGUE_SPORTS)[number];

/**
 * The settings half of a saved league: `leagueSettingsSchema`, with `teams`
 * nullable and NOTHING else changed.
 *
 * ⚠️ ONE WIDENING, AND IT IS THE HONESTY OF THE FEATURE. The CHAT contract
 * requires `teams` — a settings block reaching Franco without a league size
 * would have him say "in your 12-team league" to somebody who never said twelve.
 * But a punter mid-draft who has typed nine names and not yet decided how big
 * the league is has a roster worth SAVING, and refusing the save until they
 * state a size is exactly how "12" gets typed by someone who does not know.
 *
 * So the store accepts a null size and the wire still does not: `leagueSettingsFor`
 * returns null when `teams` is null, so nothing is sent on a chat turn and Franco
 * gets the server's own not-set block. RIPK's `kev_league_store.validate_settings`
 * documents the same single widening on the same reasoning.
 *
 * ⚠️ BUILT FROM `leagueCommon` AND `leagueSlots`, NOT RETYPED. A second literal
 * list of bounds here is a second answer to "what is a legal league", and the two
 * would drift the first time the server moved one.
 */
const savedLeagueCommon = { ...leagueCommon, teams: leagueCommon.teams.nullable() };

export const savedLeagueSettingsSchema = z.discriminatedUnion("sport", [
  z.object({
    sport: z.literal("football"),
    slots: leagueSlots(LEAGUE_SLOT_KEYS.football),
    ...savedLeagueCommon,
  }),
  z.object({
    sport: z.literal("nfl"),
    slots: leagueSlots(LEAGUE_SLOT_KEYS.nfl),
    ...savedLeagueCommon,
  }),
]);

export type SavedLeagueSettings = z.infer<typeof savedLeagueSettingsSchema>;

/**
 * One code's squad and league, as the account holds it.
 *
 * ⚠️ `revision: 0` MEANS NOTHING HAS EVER BEEN SAVED, AND IT IS A 200 NOT A 404.
 * "You have saved nothing yet" is the ordinary first state of this feature and
 * must not reach the panel down the same path as "the read failed" — an empty
 * panel that reads as "nothing saved" when the truth is "we could not tell" is
 * the one wrong thing it could say. It is also the token a write has to send
 * back: see `ifRevision` on `api.saveLeague`.
 *
 * ⚠️ NOTHING ON THIS WIRE SAYS A NAME WAS CHECKED, BECAUSE NONE WAS. `players`
 * is free text the reader typed into a form that has no player index behind it.
 * The store bounds the SHAPE — how many places, how long a name — and renders
 * each name inert for a prompt, and then hands it back exactly as typed. Saving
 * it to an account changes where it is kept and nothing about whether the person
 * exists, and the panel says so in those words.
 *
 * ⚠️ `players` KEEPS ITS EMPTY STRINGS. The index IS the place: dropping the
 * unfilled ones would slide the third receiver into the first receiver's box on
 * the next read. `features/fantasy/squad.ts` says the same about the browser
 * copy, and the two have to agree or a round trip through the account reorders
 * somebody's squad.
 */
export const savedLeagueSchema = z.object({
  sport: z.string(),
  settings: savedLeagueSettingsSchema.nullable(),
  /** Slot key (upper-case, or `BENCH`) → a name per place. "" is unfilled. */
  players: z.record(z.string(), z.array(z.string())),
  createdAt: z.string(),
  updatedAt: z.string(),
  revision: z.number().int().nonnegative(),
});

export type SavedLeague = z.infer<typeof savedLeagueSchema>;

export const savedLeagueResponseSchema = savedLeagueSchema.extend({
  /**
   * Server-owned ceilings, read rather than hardcoded — the same decision as
   * `limits` on the saved-squads response, and for the same reason: what the
   * reader is told the ceilings are and what the store enforces cannot drift.
   */
  limits: z.object({
    maxNamed: z.number().int(),
    maxPlacesPerSlot: z.number().int(),
    maxNameChars: z.number().int(),
    maxBytes: z.number().int(),
  }),
});

export type SavedLeagueResponse = z.infer<typeof savedLeagueResponseSchema>;

/** The body of a save. `ifRevision` is required — see `api.saveLeague`. */
export interface SaveLeagueInput {
  settings: SavedLeagueSettings | null;
  players: Record<string, string[]>;
  ifRevision: number;
}

export const chatUsageSchema = z.object({
  creditsCharged: z.number().int().nonnegative(),
  balanceAfter: z.number().int().nonnegative(),
});

/**
 * The wire shape of a single chat turn.
 *
 * Today's Lambda returns this whole object at once. SSE/WebSocket later will
 * stream deltas. The UI consumes an AsyncIterable<ChatEvent> either way and does
 * not know which is happening — see services/api/chat.ts.
 */
/**
 * A tool's own payload, passed straight through.
 *
 * Deliberately unvalidated beyond "it's an object". Every tool returns a different
 * shape and the server owns all of them; pinning them here would mean a backend that
 * cannot add a field without breaking the client. What the UI actually reads is
 * narrow (`error`, `linesKevCanPrice`, `did_you_mean`) and it reads defensively.
 */
export const intentResultSchema = z.record(z.string(), z.unknown());

export const chatResponseSchema = z.object({
  messageId: z.string(),
  /** Absent on a lookup — a menu read has data but no prose. */
  reply: z.string().default(""),
  toolsUsed: z.array(z.string()).default([]),
  usage: chatUsageSchema,
  /** The server always says which thread the turn landed in — a brand-new
      conversation is created server-side on the first turn, and the client
      only learns its id from this field, never by minting one itself. */
  conversationId: z.string(),
  /**
   * THE TOOL'S OWN ANSWER, when the turn was composed rather than typed.
   *
   * This exists because the model once described a tool result wrongly — it said
   * KEV had no history for a player it holds 334 games of, and the punter had no
   * way to see otherwise, because the prose was the only account of what happened.
   * When this is present the UI renders the outcome from HERE and treats `reply` as
   * the voice around it. If the two disagree, the tool wins and the disagreement is
   * visible instead of silent.
   */
  intentResult: intentResultSchema.optional(),
  /** Set when a composed intent was rejected (bad tool, wrong persona/tier). The
      turn still answered as ordinary prose — this says the structured path didn't
      run, so the UI can fall back rather than render a stale panel. */
  intentError: z.string().optional(),
});

/**
 * Chat history — the "recent conversations" list, one row per thread.
 *
 * `preview` and `title` are both server-derived (see mocks/handlers/index.ts)
 * so the list can render without fetching every conversation's full messages.
 */
export const conversationSummarySchema = z.object({
  id: z.string(),
  modelId: modelIdSchema,
  title: z.string(),
  lastActiveAt: z.iso.datetime(),
  preview: z.string(),
  turnCount: z.number().int().nonnegative(),
});

export const chatHistoryResponseSchema = z.object({
  conversations: z.array(conversationSummarySchema),
  /** Flat 24h for every tier today. Sent by the server so the UI states the real
      number rather than hardcoding one that could drift from the backend TTL. */
  retentionHours: z.number().int().positive(),
});

export const conversationMessageSchema = z.object({
  id: z.string(),
  role: z.enum(["user", "assistant"]),
  text: z.string(),
  at: z.iso.datetime(),
  creditsCharged: z.number().int().nonnegative().optional(),
});

export const conversationDetailSchema = z.object({
  id: z.string(),
  modelId: modelIdSchema,
  messages: z.array(conversationMessageSchema),
});

/* `mailSubscribeResponseSchema` was here until 2026-08-28, removed with the
 * mailing-list form that was its only caller. It carried the one piece of
 * reasoning in this file worth restating if any endpoint ever answers about an
 * email address again: the server returned an identical `{ ok: true }` for a
 * new address, an already-subscribed one, a rate-limited one and a rejected
 * one, ON PURPOSE. Anything richer is an enumeration oracle for "is this
 * address on the list", one query at a time — the same hole
 * `preventUserExistenceErrors` closes on the user pool.
 *
 * It was also the only `.strict()` schema here, because Zod strips unknown keys
 * by default and stripping would have turned a backend that started
 * distinguishing outcomes into a silent pass instead of a loud failure.
 */

export type Sport = z.infer<typeof sportSchema>;
export type Mode = z.infer<typeof modeSchema>;
export type ModelId = z.infer<typeof modelIdSchema>;
export type BuildStatus = z.infer<typeof buildStatusSchema>;
export type TierId = z.infer<typeof tierIdSchema>;
export type Capability = z.infer<typeof capabilitySchema>;
export type ModelEntry = z.infer<typeof modelEntrySchema>;
export type CreditBalance = z.infer<typeof creditBalanceSchema>;
export type TierLimits = z.infer<typeof tierLimitsSchema>;
export type EntitlementsSnapshot = z.infer<typeof entitlementsSchema>;
export type Profile = z.infer<typeof profileSchema>;
export type Consent = z.infer<typeof consentSchema>;
export type Disclaimer = z.infer<typeof disclaimerSchema>;
export type SeasonTeam = z.infer<typeof seasonTeamSchema>;
export type SeasonBoardResponse = z.infer<typeof seasonBoardResponseSchema>;
export type PlayerFormResponse = z.infer<typeof playerFormResponseSchema>;
export type PlayerFormRow = z.infer<typeof playerFormRowSchema>;
export type PlayerStatLine = z.infer<typeof playerStatLineSchema>;
export type MeResponse = z.infer<typeof meResponseSchema>;
export type ApiError = z.infer<typeof apiErrorSchema>;
export type ChatResponse = z.infer<typeof chatResponseSchema>;
export type ChatUsage = z.infer<typeof chatUsageSchema>;
export type ConversationSummary = z.infer<typeof conversationSummarySchema>;
export type ChatHistoryResponse = z.infer<typeof chatHistoryResponseSchema>;
export type ConversationMessage = z.infer<typeof conversationMessageSchema>;
export type ConversationDetail = z.infer<typeof conversationDetailSchema>;

/**
 * The free public board (teaser.json — produced daily by nba/export_teaser.py,
 * served as a static object). Win probabilities ONLY: the exporter follows the
 * chat tool's H2H contract, so anything beyond a favourite + single win %
 * (spreads, totals, margins — and the calibration RANGE, which is a
 * subscription feature living in Kev's chat answers) is absent BY DESIGN, and
 * this schema must never grow them.
 */
export const boardGameSchema = z.object({
  start: z.string(),
  home: z.string(),
  away: z.string(),
  /** Below the model's priced floor — shown anyway, flagged "Kev's Close Call". */
  closeCall: z.boolean(),
  favourite: z.string(),
  winProb: z.number(),
  /**
   * The competition, named as a reader would name it — "Premier League", not
   * "epl".
   *
   * ⚠️ THE NBA BOARD NEVER NEEDED THIS AND THE FOOTBALL ONE CANNOT DO WITHOUT
   * IT. Every NBA row is the NBA. A football board carries five competitions at
   * once, so "Sunderland v Fulham" sitting above "Levante v Real Betis" with no
   * label asks the reader to know which league each club is in.
   *
   * `.optional()` because the NBA exporter does not send it and should not have
   * to: a board with one competition has nothing to disambiguate.
   */
  league: z.string().optional(),
  /**
   * A PRESEASON row: shown and priced, but never frozen and never scored.
   *
   * The models are trained with preseason excluded — starters play half-minutes
   * and rotations are experimental — so `winProb` here is an indication of
   * relative strength rather than a read on the night. `export_teaser.py` does
   * not call `freeze()` for these, so none of them can ever reach `record`.
   *
   * Per-ROW rather than per-response because an October slate can straddle the
   * changeover: one board can hold indicative and real games at once, and a
   * whole-board caveat would tar the real ones.
   *
   * `.optional()` because a server older than 2026-08-28 never sends it, and
   * absent must mean "a normal, counted call" — which it does, since the only
   * rows that carry it are the ones the exporter deliberately excluded from the
   * ledger.
   */
  indicative: z.boolean().optional(),
});

export const boardResultSchema = boardGameSchema.extend({
  score: z.string(),
  /** Every tip settles — close calls included, or the record is cherry-picked. */
  call: z.enum(["HIT", "MISS"]),
  demo: z.boolean().optional(),
});

export const boardResponseSchema = z.object({
  generatedAt: z.string(),
  ratingsAsOf: z.string(),
  /**
   * `preseason` added 2026-08-28. It means the board is showing games, but the
   * only games it has are preseason — so every row is indicative and the record
   * has not started. One real fixture flips this to `live`, and the per-row
   * `indicative` flag keeps carrying the caveat for any preseason games beside
   * it.
   *
   * ⚠️ THIS ENUM IS WHY THE CLIENT SHIPS FIRST. A cached bundle whose schema
   * knows only `live`/`offseason` fails to PARSE a preseason board, and a failed
   * parse here is not a missing section — it is the whole page erroring. Deploy
   * this before the exporter starts emitting it.
   */
  state: z.enum(["live", "offseason", "preseason"]),
  demo: z.boolean().optional(),
  note: z.string().optional(),
  upcoming: z.array(boardGameSchema),
  results: z.array(boardResultSchema),
  /**
   * The SAME shape as `record`, for calls that were reconstructed after the
   * match rather than published before it.
   *
   * ⚠️ IT IS A SEPARATE KEY BECAUSE MERGING THE TWO WOULD FALSIFY THE
   * STANDFIRST. The board promises "what you see settled is what was said before
   * the game, never a redo". `soccer/backfill_calls.py` prices fixtures already
   * played so the season grid is complete; those calls carry `source=backfill`
   * in the ledger and land here, never in `record`. Any change that adds them
   * together makes the page's central sentence untrue.
   *
   * It is also NOT a measure of accuracy: the ratings were fitted on a window
   * that includes some of these matches. It says the grid is filled in, and
   * nothing more. Held-out numbers live in `soccer/accuracy.py` and
   * `soccer/epl_board.py`.
   */
  backfilled: z
    .object({
      tips: z.number(),
      hits: z.number(),
      byLeague: z
        .array(z.object({ league: z.string(), tips: z.number(), hits: z.number() }))
        .optional(),
    })
    .nullable()
    .optional(),
  /**
   * True when `record` counts reconstructed calls as well as published ones.
   *
   * ⚠️ WHEN THIS IS SET, THE STANDFIRST IS WRONG UNLESS THE PAGE SAYS OTHERWISE.
   * The board promises "what you see settled is what was said before the game,
   * never a redo, and every tip counts in the record". With this on, 41 of
   * football's 52 settled calls were priced AFTER the match — so `BoardPanel`
   * renders a qualifier in the same breath as the promise, exactly as it already
   * does for preseason rows.
   *
   * Set by `soccer/export_teaser_football.py --combine-record`, off by default
   * (Ned, 2026-08-31). What made it allowable is that merging LOWERS the headline
   * — 11-4 published, 41-36 reconstructed, 52-40 combined — so it understates
   * rather than flatters.
   *
   * READ FROM THE PAYLOAD, never hardcoded per sport: turning the flag off then
   * restores the strong promise on its own, and the page only ever claims what it
   * was actually served. NBA has no backfill and is unaffected either way.
   */
  recordIncludesReconstructed: z.boolean().optional(),
  /**
   * Both halves of that combined number, so the page can show what it is made of
   * rather than asking the reader to take it on trust.
   */
  recordSplit: z
    .object({
      published: z.object({ tips: z.number(), hits: z.number() }).nullable().optional(),
      reconstructed: z.object({ tips: z.number(), hits: z.number() }).nullable().optional(),
    })
    .optional(),
  record: z
    .object({
      tips: z.number(),
      hits: z.number(),
      /**
       * The same record, split by competition.
       *
       * One number across five leagues is the average of whichever ones happened
       * to settle that week, and it lets a strong record in one carry a weak one
       * in another indefinitely — the same cherry-picking the whole-history rule
       * prevents by date, sliced by league instead.
       *
       * The parts sum to `tips`/`hits` by construction; the exporter counts them
       * in one pass. Ordered by competition NAME, never by hit rate — sorting
       * our own accuracy by how well we did would put the best number first on a
       * page whose argument is that we do not choose which results to show.
       *
       * `.optional()` for the NBA, which has one competition and no breakdown to
       * make.
       */
      byLeague: z
        .array(z.object({ league: z.string(), tips: z.number(), hits: z.number() }))
        .optional(),
    })
    .nullable(),
});

/**
 * The Premium per-fixture board — every cleared market for one upcoming match,
 * with its fair price.
 *
 * ⚠️ THIS IS THE PAID SIDE OF THE SAME DATA THE FREE BOARD SHOWS ONE LINE OF.
 * `boardResponseSchema` above carries ONE market per fixture on purpose: double
 * chance, draw-no-bet, correct score and the margin book are Premium, and a free
 * board quoting them would give the product away (guardrails §5.1). Two schemas
 * describing the same fixtures differently IS the product boundary, not
 * duplication — do not merge them.
 *
 * `fairOdds` is 1/p and NOTHING ABOUT A BOOKMAKER. Against the closing line the
 * model measures 3.1% behind, so a price here that looks generous next to a
 * bookmaker's is a warning rather than value. `null` where the probability is
 * too small to price: 1/0.001 is arithmetic, but printing 1000.00 implies a
 * precision the tail of a fitted distribution does not have.
 */
export const fixtureMarketSchema = z.object({
  market: z.string(),
  /** result | double chance | draw no bet | winning margin | correct score */
  group: z.string(),
  /** Named with the clubs — "Arsenal to win", never "home_win". */
  label: z.string(),
  p: z.number(),
  fairOdds: z.number().nullable(),
  /** Correct score only: the scoreline the probability refers to. */
  score: z.string().optional(),
});

export const fixtureSchema = z.object({
  fixtureKey: z.string(),
  kickoff: z.string(),
  league: z.string(),
  home: z.string(),
  away: z.string(),
  expectedGoalsHome: z.number().nullable().optional(),
  expectedGoalsAway: z.number().nullable().optional(),
  ratingsAsOf: z.string().nullable().optional(),
  /** Clubs whose rating is carried across from the division below. */
  promotedClubs: z.array(z.string()).default([]),
  markets: z.array(fixtureMarketSchema),
});

export const fixtureBoardResponseSchema = z.object({
  generatedAt: z.string(),
  ratingsAsOf: z.string(),
  fixtures: z.array(fixtureSchema),
});

export type FixtureMarket = z.infer<typeof fixtureMarketSchema>;
export type Fixture = z.infer<typeof fixtureSchema>;
export type FixtureBoardResponse = z.infer<typeof fixtureBoardResponseSchema>;

export type BoardGame = z.infer<typeof boardGameSchema>;
export type BoardResult = z.infer<typeof boardResultSchema>;
export type BoardResponse = z.infer<typeof boardResponseSchema>;

/**
 * The per-fixture PAGE — Kev's full read on one match, one level deeper than
 * `fixtureBoardResponseSchema` above. That schema is every match's cleared
 * markets in a row each; this is ONE match's whole case: the call in prose,
 * how it compares to the market, where the value sits, how the market has
 * moved, every prop line for both clubs, team news and the head-to-head guide.
 *
 * `GET /football/fixture/{key}` serves one of these. `?artifact=football-
 * fixture-pages` on `/season/board` (below) serves the coming week's worth at
 * once, the same "one route, several gated artifacts" idiom `fixtureBoard`
 * above already uses and for the same deploy-ordering reason.
 *
 * PERMISSIVE ON PURPOSE — the `resetAt` lesson at the top of this file, applied
 * to a page with eight independent sections: `fixture` and `read` are the
 * load-bearing minimum, and everything else is `.optional()` so a page missing
 * one section (say, no market movement has been recorded yet) renders seven
 * sections rather than throwing at the boundary and showing none of them.
 */
export const fixturePageFixtureSchema = z.object({
  key: z.string(),
  kickoff: z.string(),
  /**
   * ⚠️ `league` OR `competition`, AND NEITHER IS REQUIRED — 2026-09-08.
   *
   * Football's exporter writes `league: "seriea"`. The NFL's writes
   * `competition: "nfl"` beside `sport: "nfl"`, which is the spelling the
   * PUBLIC file, the free file and `publicFixtureSchema` have always used —
   * `league` is the football gated page's own name for it and nothing else in
   * the product says `league`. Held to a required `league` this rejected all
   * sixteen NFL pages at the boundary, which on a single-document fetch is the
   * whole page.
   *
   * FIXED HERE RATHER THAN IN THE NFL EXPORTER, unlike `result.rows[].label`
   * below it. Adding `league` to the NFL document would spread football's
   * odd-one-out name to a second sport to satisfy a client field, which is
   * changing a key to fix a label. `competitionOf()` reads whichever is
   * present.
   */
  league: z.string().optional(),
  competition: z.string().optional(),
  /** Which code this page belongs to. Absent on football's document, which is
      why it is optional — the route already knows, and this is a
      cross-check rather than the source of truth. */
  sport: z.string().optional(),
  home: z.string(),
  away: z.string(),
  /* NULLISH, not optional: the exporter writes `venue: null` when the lake has
     no venue for a fixture, and `.optional()` alone rejects an explicit null —
     the third field in this file to lose a whole page that way. */
  venue: z.string().nullish(),
  /* ── THE NFL DOCUMENT'S OWN FIXTURE FIELDS ────────────────────────────────
     All optional, because football sends none of them. They are carried rather
     than stripped for the reason the key-retention walk in
     `productionGated.test.ts` exists: a field the server grows and the client
     silently deletes is the quiet half of contract drift. */
  venueCity: z.string().nullish(),
  /** Already formatted by the exporter, in the reader's own timezone. */
  kickoffAEST: z.string().nullish(),
  week: z.number().int().nullish(),
  weekLabel: z.string().nullish(),
  /**
   * A game with a designated home CLUB but no home-field edge — `_price_matchup`
   * prices it with HFA=0. Carried so the page can say "not a home game for
   * either side" rather than showing a home team the reader can see is playing
   * somewhere else.
   */
  neutralSite: z.boolean().nullish(),
  neutralSiteRule: z.string().nullish(),
  /** The home-field points the rating actually applied. `null` when the market
      could not be priced at all. */
  hfaApplied: z.number().nullish(),
});

/** The fixture's competition slug, whichever of the two names the document
    used. Neither is guaranteed — see `fixturePageFixtureSchema.league`. */
export function competitionOf(
  fixture: Pick<z.infer<typeof fixturePageFixtureSchema>, "league" | "competition">,
): string | undefined {
  return fixture.competition ?? fixture.league;
}

/** 01 — Kev's read, in his own words. */
export const fixturePageReadSchema = z.object({
  text: z.string(),
  generatedAt: z.string(),
});

/**
 * 02 — one side of the result, Kev against the market average.
 *
 * `marketP` here is the MARKET'S probability, mirroring `fixtureMarketSchema`'s
 * `p` — paired with `kev` the same way that schema pairs a probability with a
 * `fairOdds`. `gap` is `kev − marketP` IN PERCENTAGE POINTS — the exporter
 * writes `round((kev - marketP) * 100, 2)`, the same unit its own `FLAG_PTS`
 * threshold of 5 is in, so a gap of `10.93` is ten and a bit points. It is
 * carried on the wire rather than derived client-side so the exporter's
 * rounding is what the page shows; `gapDisplay` was reading it as a raw
 * probability and printing "+1093.0" until 2026-09-08.
 *
 * ⚠️ `marketP`, NOT `market` — CONTRACT UPDATE 2026-09-07. This field was
 * built as bare `market`, which the real exporter's brief never confirmed and
 * which collided with `fixtureValueRowSchema`'s own `market` (that one holds
 * the market's machine id, e.g. `"btts_yes"`, not a probability). The gated
 * page's contract settled on `marketP` for the probability in both places —
 * see the sibling note on `fixtureValueRowSchema` below — so this row uses the
 * same name a merge against the free file's `fairOdds`-only rows can key off
 * without a second rename living only here.
 */
export const fixtureResultRowSchema = z.object({
  side: z.enum(["home", "draw", "away"]),
  label: z.string(),
  /** The BARE club name, which the label is not. NFL sends it beside the
      label so the swatch and any name match key off a club rather than off a
      sentence; football does not, hence optional. */
  team: z.string().nullish(),
  kev: z.number(),
  /**
   * `[low, high]` — KEV's own uncertainty on this side, in probability.
   *
   * FREE-TIER CONTENT, not a market comparison: it is how sure KEV is of his
   * own number, which is why `FREE_FORBIDDEN_KEYS` in both exporters has never
   * held it. Only the NFL document carries one today. Rendered as a band
   * beside the probability, never as a second number to bet into.
   */
  kevRange: z.tuple([z.number(), z.number()]).optional(),
  marketP: z.number().nullable(),
  gap: z.number().nullable(),
  bestPrice: z.number().nullable(),
  flagged: z.boolean(),
});

/**
 * ⚠️ THE H2H GATE, ON THE WIRE — 2026-09-08, and the reason it is in the
 * SCHEMA rather than derived from the rows.
 *
 * `lambda_nfl_chat._price_matchup` prices an NFL winner only when the
 * favourite clears a 70% floor; on the 2026 week-1 slate that is 3 games of
 * 16. What it refuses is THE PICK, NOT THE NUMBER — a refused fixture still
 * carries both sides' probability, band and fair odds (the free tier's own
 * content) and carries `favourite: null`, `pickRefused: true` and the tool's
 * own `why` string.
 *
 * A client that only saw `rows` would have exactly one way to render a refused
 * game: pick the higher number and call it the favourite. That is routing
 * around the gate — the model's own measurement is that the 0.5–0.7 zone does
 * not calibrate, so a page naming a winner there is making a claim the model
 * declined to make. These four fields are what make the refusal renderable, so
 * they are required to arrive rather than inferred.
 *
 * All optional because football's document carries none of them: football
 * prices every fixture and has no gate to report.
 */
export const fixtureResultSchema = z.object({
  rows: z.array(fixtureResultRowSchema),
  /** The tool's own flag, read rather than re-derived. */
  priced: z.boolean().optional(),
  /** The side KEV will name — `null` on a refused game, and the page must not
      substitute the higher row for it. */
  favourite: z.string().nullish(),
  pickRefused: z.boolean().optional(),
  /** The tool's own refusal sentence, carried verbatim. Absent on a priced
      game (the exporter omits the key rather than sending an empty string). */
  whyNoPick: z.string().nullish(),
  /** The model could not price the matchup at all — no rows, and not the same
      thing as a refused pick, which has them. */
  unpriceable: z.boolean().optional(),
  modalScore: z.object({ score: z.string(), p: z.number() }).optional(),
  xg: z.tuple([z.number(), z.number()]).optional(),
});

/**
 * 03 — one market's edge: Kev's own probability against the market average.
 *
 * ⚠️ `marketP`, NOT `market` twice — CONTRACT UPDATE 2026-09-07. The design
 * brief this schema was built from named the field `market` on this row for
 * both the market's machine id AND its probability — impossible on the wire,
 * where a JSON object key can appear only once. `market` keeps the id
 * (`"btts_yes"`); the probability was first spelled `marketProb` here (flagged
 * verbatim in this repo's report as a contract question for whoever specs the
 * real exporter) and the answer that came back settled on `marketP` instead —
 * the same name `fixtureResultRowSchema` uses for the identical quantity one
 * section up, so a reader (and a merge against the free file) sees one
 * spelling for "the market's probability" rather than two.
 */
export const fixtureValueRowSchema = z.object({
  market: z.string(),
  label: z.string(),
  /**
   * ⚠️ NULLABLE, 2026-09-08 — a real state, so fixed here rather than in the
   * exporter.
   *
   * The NFL document's third value row is the game TOTAL ("Total: 44.2
   * points"), and a total centre is not a probability. `_build_value` writes
   * `kev: null` for it deliberately, sorted to the bottom with no market
   * reading at all. Held to `z.number()` this rejected the row, the row
   * rejected the DOCUMENT, and a single-document fetch has no per-row
   * tolerance — one informational line would have cost every Premium section
   * on all sixteen pages. `ValueSection` renders it as an em dash; it must
   * never render a null as `0.0%`.
   */
  kev: z.number().nullable(),
  marketP: z.number().nullable(),
  gap: z.number().nullable(),
  flagged: z.boolean(),
  /**
   * A 0–100 edge-meter width for the row's bar. Absent rows render no meter.
   *
   * ⚠️ OPEN, 2026-09-08: `export_fixture_pages.py` sends a constant `FLAG_PTS`
   * (5.0) on every value row, so every meter in the published file is the same
   * 5%-wide sliver — including on a row flagged at 10.9 points. That is a
   * meter that says nothing, not a wrong number, so nothing here coerces it;
   * what the width should be is a product decision, not a parse one. Named
   * rather than left to be noticed.
   */
  band: z.number().optional(),
});

export const fixtureValueSchema = z.object({
  rows: z.array(fixtureValueRowSchema),
});

/** 03 — the edge record strip beside the value section: how flagged calls on
    this fixture's market have actually settled. */
/**
 * ⚠️ EVERY DERIVED FIELD HERE IS NULLABLE, AND ALL OF THEM ARE NULL TODAY.
 *
 * Everything computed FROM settled calls (`since`, `units`, `roi`,
 * `beatClose`) is null until something has actually settled, because 0 units
 * off no bets would read as breaking even. Held to `z.number()` these rejected
 * every row of the published file and the landing rendered "the fixture list is
 * unavailable right now" — a whole page lost to a strict optional. Same shape
 * of bug as `publicFixtureValueSchema.count`, found the same way: by parsing
 * the live payload instead of the mock.
 *
 * ⚠️ `flagged` AND `settled` ARE NULLABLE TOO, ADDED 2026-09-07, AND THE NULL
 * IS NOT A TIDINESS ALLOWANCE. This block used to say the two counts "are
 * always numbers — zero is a true count", which is exactly right for a sport
 * whose edge ledger exists: 0/0 there means Kev has flagged nothing yet out of
 * nothing settled. NFL has no edge ledger at all, so the exporter writes null
 * — "there is no record", which is a different claim from "the record is
 * zero", the same distinction `publicFixtureValueSchema.count` and `units` /
 * `roi` / `since` above it already draw. THE EXPORTER MUST NOT COERCE THESE TO
 * 0 to make the type simpler; `EdgeRecordStrip` renders null as an em dash,
 * beside the other three that already do.
 *
 * This was the fourth production shape in one day (after `value.count`,
 * `units`/`roi`/`since` and `venue`) to lose the WHOLE landing page over one
 * row — see `fixturesPublicResponseSchema` for the per-row tolerance that
 * finally makes the fifth one cost a row instead of a page.
 */
export const fixtureEdgeRecordSchema = z.object({
  since: z.string().nullable(),
  flagged: z.number().int().nullable(),
  settled: z.number().int().nullable(),
  units: z.number().nullable(),
  roi: z.number().nullable(),
  beatClose: z.number().nullable(),
  /** False below a sample the exporter will not call representative — the
      page renders the "n too small" badge rather than let ten settled calls
      read as a track record. */
  sampleOk: z.boolean(),
});

/** 04 — one side's price movement since the market opened on this fixture. */
export const fixtureMovementRowSchema = z.object({
  side: z.enum(["home", "draw", "away"]),
  label: z.string(),
  first: z.number(),
  now: z.number(),
  movePts: z.number(),
  /** Oldest first, for the inline sparkline — same convention as
      `seasonTeamSchema.playoffHistory`. */
  series: z.array(z.number()),
});

export const fixtureMovementSchema = z.object({
  /**
   * NULLABLE, 2026-09-08 — and this one is the SCHEMA's bug, not the
   * exporter's.
   *
   * `since` is the timestamp of the oldest price read on file, so it cannot
   * exist until there is a read. `export_fixture_pages.py` publishes
   * `{since: null, reads: 0, rows: [], volume: false}` for a fixture the
   * cross-account odds feed has no history for — all 52 football fixtures
   * today, because the bronze snapshots cover EPL only. Held to `z.string()`
   * it rejected the object, the object rejected the DOCUMENT, and a Pro
   * reader lost the whole page. Same shape of bug as `edgeRecord.units` /
   * `roi` / `since` above: a derived field that is honestly absent until
   * something has been recorded.
   *
   * `MovementSection` treats this empty-sentinel object the same as an absent
   * `movement` — "No price history recorded for this fixture yet" — rather
   * than dating "0 reads since" off a null, which `new Date(null)` would
   * render as 1 January 1970.
   */
  since: z.string().nullable(),
  reads: z.number().int(),
  volume: z.boolean(),
  rows: z.array(fixtureMovementRowSchema),
});

/** 05 — one player prop line, for the strip under each club. */
export const fixturePagePropRowSchema = z.object({
  player: z.string(),
  stat: z.string(),
  line: z.number(),
  label: z.string(),
  /** `[low, high]`, same shape and same reason as `props/fairPrice.ts`'s
      `ProbRange` — a range is the answer, never a point estimate. */
  band: z.tuple([z.number(), z.number()]),
  fair: z.number(),
  games: z.number().int(),
});

/**
 * ⚠️ `available` / `why` — AN EMPTY LIST AND A REFUSAL ARE DIFFERENT CLAIMS.
 *
 * Football sends prop rows. The NFL document sends `{available: false, why:
 * …, home: [], away: []}` on every fixture, and the reason is a real one: there
 * is no NFL multi-menu tool and the 2026 roster feed is empty, so the only club
 * a player can be attributed to is the one he LAST played for. Naming props off
 * that would put players on teams they have left.
 *
 * Without these two fields the client saw `{home: [], away: []}` — which reads
 * as "Kev looked and nothing qualified", the same absent-versus-negative
 * conflation `publicFixtureValueSchema.count` and `edgeRecord.flagged` already
 * draw a line under. `PropsSection` renders the reason instead of an empty box.
 */
export const fixturePagePropsSchema = z.object({
  /** Absent on football, which means "yes" by omission — a document that says
      nothing about availability is one that shipped rows. */
  available: z.boolean().optional(),
  why: z.string().nullish(),
  home: z.array(fixturePagePropRowSchema).default([]),
  away: z.array(fixturePagePropRowSchema).default([]),
});

/* ── SECTIONS ONLY THE NFL DOCUMENT CARRIES ────────────────────────────────
 *
 * The NFL page has LESS than football's in the four places that matter — no
 * odds feed, so no market comparison, no movement and no edge ledger; no prop
 * board; no meeting history — and MORE in three, because the NFL model answers
 * questions football's does not. Those three are below.
 *
 * Every one of them carries the tool's OWN refusal language rather than a
 * paraphrase, which is the point: `margin` exists to be shown next to the
 * sentence saying it is not a spread price. */

/**
 * The margin lean — INFORMATIONAL, and it must never render as a spread.
 *
 * `tool_game_market` returns a lean ("Seattle Seahawks by 5.1") together with
 * `marginIsALean`, which says in as many words that the model misses the margin
 * by about ten points and that KEV does not price the NFL spread. Both travel
 * verbatim, and `spread` is the tool's refusal string, not a number.
 */
export const fixtureMarginSchema = z.object({
  lean: z.string().nullish(),
  note: z.string().nullish(),
  spread: z.string().nullish(),
});

export const fixtureTotalBandSchema = z.object({
  confidence: z.string(),
  range: z.string(),
  /** How often the real total actually landed inside this band, on seasons the
      width was not estimated from. The width is the claim; this is the
      evidence for it. */
  measuredCoverage: z.number().nullish(),
});

/** The game total — a RANGE with measured coverage, or the tool's refusal.
    Never compared to a market, because there is no NFL market to compare to. */
export const fixtureTotalSchema = z.object({
  priced: z.boolean().optional(),
  centre: z.string().nullish(),
  bands: z.array(fixtureTotalBandSchema).default([]),
  how: z.string().nullish(),
  sellingRule: z.string().nullish(),
  /** Present instead of the rest when the tool refused a total outright. */
  why: z.string().nullish(),
});

/** One leg of a suggested multi. Every NFL multi is CROSS-GAME — the tool
    prices one market per game, so a same-game NFL multi has one leg, which is
    a single. */
export const fixtureMultiLegSchema = z.object({
  fixtureKey: z.string(),
  week: z.number().int().nullish(),
  side: z.enum(["home", "draw", "away"]),
  team: z.string(),
  opponent: z.string().nullish(),
  label: z.string(),
  kickoff: z.string().nullish(),
  kev: z.number(),
  fairOdds: z.number().nullable(),
  /** The market's machine id. GATED ONLY — `_free_multis` drops it, so this is
      optional and a free-tier leg simply has none. */
  market: z.string().optional(),
});

/**
 * The measured correlation behind a suggestion. PREMIUM ONLY — the free file
 * keeps the plain-English `note` and drops this, because a coefficient is the
 * working and the working is what Premium buys.
 *
 * `rho` is what was SHIPPED (the bottom of the measured interval); `fittedRho`
 * is what was measured. Both are carried so the page can show that the price
 * is the conservative end rather than the point estimate.
 */
export const fixtureMultiCorrelationSchema = z.object({
  rho: z.number(),
  fittedRho: z.number().nullish(),
  ci95: z.tuple([z.number(), z.number()]).nullish(),
  legsMeasured: z.number().int().nullish(),
  shippedAt: z.string().nullish(),
  how: z.string().nullish(),
});

export const fixtureMultiSuggestionSchema = z.object({
  legs: z.array(fixtureMultiLegSchema),
  kev: z.number(),
  fairOdds: z.number(),
  /** "same-week" or "cross-week" — which measured regime priced it. */
  basis: z.string().nullish(),
  method: z.string().nullish(),
  /** How much the correlation moved the price against multiplying the legs.
      The naive independent product itself deliberately never travels. */
  correlationEdge: z.number().nullish(),
  correlation: fixtureMultiCorrelationSchema.nullish(),
  note: z.string().nullish(),
});

/**
 * Suggested multis. `available: false` plus a SPECIFIC reason, never an empty
 * list: "no multis here" reads identically whether the cause is this fixture's
 * refused pick, the rest of the slate having none, or no priceable pairing —
 * and those are three different facts.
 */
export const fixtureMultisSchema = z.object({
  available: z.boolean(),
  why: z.string().nullish(),
  maxLegs: z.number().int().nullish(),
  ranking: z.string().nullish(),
  note: z.string().nullish(),
  suggestions: z.array(fixtureMultiSuggestionSchema).default([]),
});

/**
 * The NFL SAME-GAME builder (2026-09-21): the page's legs and every 2-4 leg
 * combination of them, PRICED BY THE EXPORTER through `nfl/multis.py`.
 *
 * The page looks a combination up; it never computes one. These legs move together
 * inside a game (measured over three seasons), so a browser-side product of their
 * probabilities would be the exact mispricing the builder exists to avoid.
 *
 * `combos` is keyed by the chosen leg ids, ascending, joined by "-". A combination
 * is priced (`kev`, `fairOdds`) or refused, and `note`/`refused` index into `texts`
 * — the exporter de-duplicates the sentences so ~250 combinations stay small.
 *
 * The free file carries only `available`/`why`: the builder itself is Premium.
 */
export const fixtureSameGameLegSchema = z.object({
  id: z.number().int(),
  kind: z.enum(["win", "prop"]),
  side: z.enum(["home", "away"]),
  team: z.string().nullish(),
  label: z.string(),
  kev: z.number(),
  fairOdds: z.number().nullable(),
});

export const fixtureSameGameComboSchema = z.union([
  z.object({
    kev: z.number(),
    fairOdds: z.number(),
    edge: z.number().nullish(),
    note: z.number().int().nullish(),
  }),
  z.object({ refused: z.number().int(), verdict: z.string().nullish() }),
]);

export const fixtureSameGameSchema = z.object({
  available: z.boolean(),
  why: z.string().nullish(),
  maxLegs: z.number().int().nullish(),
  note: z.string().nullish(),
  legs: z.array(fixtureSameGameLegSchema).default([]),
  texts: z.array(z.string()).default([]),
  combos: z.record(z.string(), fixtureSameGameComboSchema).default({}),
});

/**
 * Is there a market reading on this page at all, and why not.
 *
 * ⚠️ THIS USED TO BE ON `DELIBERATELY_DROPPED` in `productionGated.test.ts` —
 * football's `{stale, refreshedAt}` was read and thrown away, named as an open
 * item. The NFL document adds `note`, and on the NFL it is not an operator
 * signal but the ANSWER to the most obvious question the page raises: KEV
 * gives a probability and no price to compare it against. "No odds source is
 * connected for the NFL" is the honest reason, and it renders.
 */
export const fixtureMarketStatusSchema = z.object({
  stale: z.boolean(),
  refreshedAt: z.string().nullish(),
  note: z.string().nullish(),
});

/** 06 — one club's team news. */
export const fixtureTeamNewsClubSchema = z.object({
  out: z.array(z.string()).default([]),
  rotation: z.array(z.string()).default([]),
  pushing: z.array(z.string()).default([]),
  nailedOn: z.array(z.string()).optional(),
});

export const fixtureTeamNewsSchema = z.object({
  /**
   * NULLISH, 2026-09-08. `_build_team_news` copies the provider's own
   * `injurySnapshotDate` through and writes `null` when the artefact carries
   * none, with `ageDays` null beside it — it cannot date a snapshot it was not
   * given. Every NFL page in today's build has both, which is exactly why this
   * is widened now rather than after the build that does not: a required
   * display field has cost this document its whole page four times.
   * `TeamNewsSection` omits the "Captured…" line rather than dating it off a
   * null.
   */
  capturedAt: z.string().nullish(),
  ageDays: z.number().nullish(),
  /**
   * How often the source behind this refreshes, in the exporter's own words —
   * "weekly snapshot, not live" on the NFL rows. Absent on football, which is
   * why it is nullish.
   *
   * It was arriving unread: the client stripped it silently, so the page said
   * "Captured today" over a snapshot that is a week old by Saturday. Not an
   * outage, which is the point — a field the server grows and the client
   * ignores is the QUIET half of contract drift, and the only reason this one
   * was noticed is `production.test.ts` walking the live payload's keys.
   */
  cadence: z.string().nullish(),
  home: fixtureTeamNewsClubSchema,
  away: fixtureTeamNewsClubSchema,
});

/** 06 — one line of the head-to-head form guide. */
export const fixtureLastFiveRowSchema = z.object({
  date: z.string(),
  text: z.string(),
});

/**
 * One club's last COMPLETED season, the NFL shape of `lastFive`.
 *
 * Every field but the two clubs themselves is nullish, deliberately. This is a
 * display block, not a calculation: a build that stops emitting `pointsForPG`
 * should cost that line of the card, never the fixture — and a whole fixture is
 * exactly what a required display field has cost this file four times now. The
 * renderer derives the headline record from `wins`/`losses`/`ties` when
 * `record` itself is absent, and shows an em dash when it can derive nothing.
 */
/** One of a club's recent completed NFL games, most recent first. */
export const fixtureRecentGameSchema = z.object({
  date: z.string().nullish(),
  opponent: z.string().nullish(),
  home: z.boolean().nullish(),
  for: z.number().int(),
  against: z.number().int(),
  /** W/L/T. The exporter sends `wl`: `result` is the gated winning-chances key,
      and the public file's leak guard refuses it anywhere in the document. The
      first build of this shape sent `result`, so both are accepted. */
  wl: z.enum(["W", "L", "T"]).nullish(),
  result: z.enum(["W", "L", "T"]).nullish(),
});

export const fixtureSeasonRecordSchema = z.object({
  /** The club's last five COMPLETED games, most recent first (Ned, 2026-09-14:
      "their last 5 matches for each team, not their last 5 head to head
      matchups"). When present the page shows these instead of the season line. */
  recent: z.array(fixtureRecentGameSchema).nullish(),
  season: z.number().int().nullish(),
  /** Already formatted by the exporter — "14-3", or "14-2-1" with a tie. */
  record: z.string().nullish(),
  wins: z.number().int().nullish(),
  losses: z.number().int().nullish(),
  ties: z.number().int().nullish(),
  games: z.number().int().nullish(),
  pointsForPG: z.number().nullish(),
  pointsAgainstPG: z.number().nullish(),
  homeRecord: z.string().nullish(),
  awayRecord: z.string().nullish(),
});

/** The NFL shape of `lastFive` — see `fixtureLastFiveSchema` below. */
export const fixtureSeasonRecordsSchema = z.object({
  /** The exporter's own sentence saying WHAT this is a record of. Rendered
      verbatim under the two clubs rather than restated here, because the
      reason there is no meeting history is a fact about the build, and the
      build is what should be allowed to change its wording. */
  basis: z.string().nullish(),
  home: fixtureSeasonRecordSchema,
  away: fixtureSeasonRecordSchema,
});

/**
 * §06's form guide — TWO SHAPES, one per sport, and the difference is real
 * rather than a bug to flatten.
 *
 * FOOTBALL ships an ARRAY: the last five times these two clubs met, which is a
 * meaningful history because European sides meet repeatedly across seasons and
 * competitions.
 *
 * NFL ships an OBJECT: each club's last completed season record. NFL sides meet
 * at most twice a year and this build's schedule holds the current season only,
 * so there is no meeting history to show — a "last five meetings" list would be
 * empty on every fixture, and an empty list reads as "they have never played"
 * rather than "this build cannot see it". The exporter says which it is in
 * `basis`, and `TeamNewsSection` renders the two shapes under two different
 * headings for the same reason.
 *
 * ⚠️ DO NOT COERCE ONE INTO THE OTHER. Flattening the season records into
 * `{date, text}` rows would make the type tidy and the page dishonest: those
 * are not meetings, and nothing in the array shape can say so.
 */
/**
 * The NBA shape of `lastFive`: each club's key players, their last five games
 * against their season baseline. Found 2026-09-22, when the NBA's opening-night
 * slate reached `fixtures_public.json` and all ten NBA rows were dropped from the
 * landing list as "unreadable" — the union below only knew football and the NFL.
 * Stats are a record, not fixed keys, so a new stat costs nothing.
 */
export const fixturePlayerFormRowSchema = z.object({
  player: z.string(),
  last5: z.record(z.string(), z.number().nullable()).nullish(),
  baseline: z.record(z.string(), z.number().nullable()).nullish(),
  formAsOf: z.string().nullish(),
});

export const fixturePlayerFormSchema = z.object({
  home: z.array(fixturePlayerFormRowSchema),
  away: z.array(fixturePlayerFormRowSchema),
});

export const fixtureLastFiveSchema = z.union([
  z.array(fixtureLastFiveRowSchema),
  fixtureSeasonRecordsSchema,
  fixturePlayerFormSchema,
]);

/** NBA player form, told apart from the NFL's per-club records by its lists. */
export function isPlayerForm(
  lastFive: z.infer<typeof fixtureLastFiveSchema>,
): lastFive is z.infer<typeof fixturePlayerFormSchema> {
  return !Array.isArray(lastFive) && Array.isArray((lastFive as { home?: unknown }).home);
}

/** A prefilled prompt for the docked chat panel. */
export const fixtureChipSchema = z.object({
  label: z.string(),
  prompt: z.string(),
});

export const fixturePageSchema = z.object({
  fixture: fixturePageFixtureSchema,
  read: fixturePageReadSchema,
  /**
   * THE PAYING READER'S READ — and the gated page's `read` is NOT it.
   *
   * `export_fixture_pages.py` writes two: `read` is the ANONYMOUS one, held to
   * a digit-free sentence ("both teams have a fair number out"), and
   * `readFull` is the account tier's ("five out apiece, projected 1-0"). The
   * free file's `read` IS this `readFull` — `derive_free_fixture` copies it
   * across — so with this field missing from the schema it was stripped here,
   * and `FixturePage`'s `gatedData?.read ?? free?.read` handed a Pro account
   * the ANONYMOUS read in place of the fuller one it had already fetched.
   *
   * Nobody saw that, because until 2026-09-08 nothing gated parsed at all. It
   * would have become visible the moment the paywall bug was fixed, which is
   * why it is fixed in the same change and not left as a follow-up.
   */
  readFull: fixturePageReadSchema.optional(),
  result: fixtureResultSchema,
  value: fixtureValueSchema.optional(),
  edgeRecord: fixtureEdgeRecordSchema.optional(),
  /** `null` is a real state — no market history recorded yet for this fixture —
      distinct from `.optional()`, which covers a server that predates the
      section entirely. Section 04 renders neither claim as "not available". */
  /* CAUGHT, 2026-09-15. Rows arrived as `home_win` with timestamp pairs, the
     parse rejected the whole page and Pro readers saw the Premium paywall on
     17 fixtures. A malformed movement block now reads as "no movement yet". */
  movement: fixtureMovementSchema.nullable().optional().catch(null),
  props: fixturePagePropsSchema.optional(),
  teamNews: fixtureTeamNewsSchema.optional(),
  /** Football's meeting history, the NFL's per-club season records, or the
      NBA's player form — see `fixtureLastFiveSchema`.
      NOT caught here, unlike the two list schemas: on the gated page a shape
      nobody checked fails loudly, by decision — see "one bad field still costs
      the whole page" in productionGated.test.ts. The lists tolerate it because
      there the cost is a whole game vanishing from the landing page. */
  lastFive: fixtureLastFiveSchema.default([]),
  chips: z.array(fixtureChipSchema).default([]),
  /* The three sections only the NFL document carries, plus the two strings
     that say where its numbers came from. Optional throughout: football sends
     none of them, and a page missing one renders the rest. */
  margin: fixtureMarginSchema.optional(),
  total: fixtureTotalSchema.optional(),
  multis: fixtureMultisSchema.optional(),
  /* Caught, for the reason `movement` above is: a new block must never be able to
     take the whole page down. A malformed builder reads as "no builder here". */
  sameGame: fixtureSameGameSchema.nullable().optional().catch(null),
  market: fixtureMarketStatusSchema.optional(),
  /**
   * The pricing tool's own caveat string, verbatim — which on the NFL says out
   * loud that the ratings end at last season's close and are therefore weakest
   * at week 1. Rendered rather than paraphrased: it is the provenance of every
   * number above it, and a paraphrase of a caveat is a softer caveat.
   */
  modelCaveats: z.string().nullish(),
  /** When the ratings behind this page were last refit. */
  ratingsAsOf: z.string().nullish(),
});

export const fixturePagesBoardResponseSchema = z.object({
  generatedAt: z.string(),
  ratingsAsOf: z.string(),
  /** Keyed by the same fixture key `GET /football/fixture/{key}` takes. */
  pages: z.record(z.string(), fixturePageSchema),
});

/**
 * The free file — `GET /teaser?artifact=fixtures-free`, PUBLIC, no auth.
 *
 * kev-docs/kev-football-fixture-page.md's "The free file" section draws the
 * line this schema enforces: Kev's own numbers are free, anything that
 * compares Kev to the market is Premium. So this is NOT `fixturePageSchema`
 * with fields dropped — it is a deliberately narrower shape with no
 * `marketP`, `gap`, `bestPrice`, `band` or `movement` anywhere in it, matching
 * `FORBIDDEN_MARKET_KEYS` in `handler_teaser.py`, which refuses to serve this
 * artifact at all if one of those keys turns up in it server-side.
 *
 * The fixture page reads this FIRST, unconditionally, and fills the gated
 * fields in from `fixturePageSchema` afterwards only when the account is
 * entitled — see `useFreeFixtures` and `FixturePage.tsx`.
 */
export const freeFixtureResultRowSchema = z.object({
  side: z.enum(["home", "draw", "away"]),
  label: z.string(),
  /** See `fixtureResultRowSchema.team`. */
  team: z.string().nullish(),
  kev: z.number(),
  /** KEV's own uncertainty on his own number — free-tier content, and the
      reason `FREE_FORBIDDEN_KEYS` has never held it. See the gated row. */
  kevRange: z.tuple([z.number(), z.number()]).optional(),
  /** Kev's own number turned into odds — never a market price, so this is
      free where `fixtureResultRowSchema.bestPrice` is gated. */
  fairOdds: z.number().nullable(),
});

/** ⚠️ THE GATE TRAVELS INTO THE FREE TIER, and it has to. The free file is
    what an account-tier reader's page renders from before entitlement
    resolves; without these fields it would show two probabilities and no
    indication that KEV declined to pick between them. Same four fields, same
    meaning, as `fixtureResultSchema` above. */
export const freeFixtureResultSchema = z.object({
  rows: z.array(freeFixtureResultRowSchema),
  priced: z.boolean().optional(),
  favourite: z.string().nullish(),
  pickRefused: z.boolean().optional(),
  whyNoPick: z.string().nullish(),
  modalScore: z.object({ score: z.string(), p: z.number() }).optional(),
  xg: z.tuple([z.number(), z.number()]).optional(),
});

/** A prop line reduced to the player's name — the gated line, market and band
    are what Premium adds on top of this same row. */
export const freeFixturePropNameSchema = z.object({ player: z.string() });

export const freeFixturePropsSchema = z.object({
  /** See `fixturePagePropsSchema` — an empty list and a refusal are different
      claims, and the NFL free file makes the same distinction the gated one
      does. */
  available: z.boolean().optional(),
  why: z.string().nullish(),
  home: z.array(freeFixturePropNameSchema).default([]),
  away: z.array(freeFixturePropNameSchema).default([]),
});

export const freeFixtureSchema = z.object({
  key: z.string(),
  kickoff: z.string(),
  /** `league` OR `competition` — see `fixturePageFixtureSchema.league`. Held
      to a required `league` this dropped all sixteen NFL rows through the
      per-row tolerance below, which is a silent, console-only failure. */
  league: z.string().optional(),
  competition: z.string().optional(),
  sport: z.string().optional(),
  home: z.string(),
  away: z.string(),
  /* The NFL free file's own fixture fields, mirroring the gated document's. */
  venueCity: z.string().nullish(),
  kickoffAEST: z.string().nullish(),
  week: z.number().int().nullish(),
  weekLabel: z.string().nullish(),
  neutralSite: z.boolean().nullish(),
  /* NULLISH, not optional: the exporter writes `venue: null` when the lake has
     no venue for a fixture, and `.optional()` alone rejects an explicit null —
     the third field in this file to lose a whole page that way. */
  venue: z.string().nullish(),
  read: fixturePageReadSchema,
  result: freeFixtureResultSchema,
  /**
   * How many Premium value rows are flagged on this fixture — never WHICH
   * ones, that is the locked panel's whole reason to exist.
   *
   * ⚠️ NULLABLE, 2026-09-08. `null` is "nothing was compared", which is not
   * `0` = "compared, none flagged" — the NFL has no odds board, so no NFL
   * fixture has ever been compared to a market and a `0` here would be a
   * measurement nobody took. `valueLockedNote` already treats `undefined` as
   * "no claim to summarise" and now treats `null` the same way.
   */
  flaggedCount: z.number().int().nullable(),
  props: freeFixturePropsSchema.optional(),
  teamNews: fixtureTeamNewsSchema.optional(),
  /** Football's meeting history OR the NFL's per-club season records — see
      `fixtureLastFiveSchema`'s own note on why the two shapes stay distinct. */
  /* Caught: a form guide the page can't read costs the form guide, never the
     fixture (2026-09-22 — a new NBA shape hid all ten NBA games). */
  lastFive: fixtureLastFiveSchema.default([]).catch([]),
  /** Disclosure, not product (Ned, 2026-09-07) — the settled record stays on
      the free view even though the value rows it is a record OF do not. */
  edgeRecord: fixtureEdgeRecordSchema.optional(),
  /** Shown locked on the free view — the labels are free, the chat they open
      is Premium. */
  chips: z.array(fixtureChipSchema).default([]),
  /* The NFL free file's copies of the three NFL-only sections. `multis` here is
     the FREE view of them: the selections, KEV's chance and the fair price,
     with `market`, `method` and the correlation dropped by the exporter — the
     working is what Premium buys. The same schema parses both because every
     dropped field is optional in it. */
  margin: fixtureMarginSchema.optional(),
  total: fixtureTotalSchema.optional(),
  multis: fixtureMultisSchema.optional(),
  /* Free view: `available`/`why` only — enough to say a builder exists. */
  sameGame: fixtureSameGameSchema.nullable().optional().catch(null),
  modelCaveats: z.string().nullish(),
});

/* ── ONE BAD ROW MUST NOT COST THE OTHER SIXTY-SEVEN ──────────────────────
 *
 * On 2026-09-07 the landing page broke FOUR TIMES in one day, and every time
 * the mechanism was identical: the exporter published a shape the schema did
 * not allow (`value.count: null`, then `units`/`roi`/`since: null`, then
 * `venue: null`, then the NFL rows' `lastFive` object and null edge counts),
 * `z.array(rowSchema)` rejected the row, the array rejected the DOCUMENT, and
 * `request()` threw — so 52, then 68, perfectly readable fixtures rendered as
 * "The fixture list is unavailable right now", with a 200 in the network tab
 * and nothing in the console.
 *
 * Widening the schema fixes the shape that broke today. It does not fix the
 * class, because the class is the all-or-nothing parse: every future field the
 * exporter adds, renames or nulls is one more chance to take the whole page
 * down, and the exporter is in another repo on another release cadence.
 *
 * So the list endpoints parse PER ROW. A row that fails is dropped and
 * reported; the rest render. Three things make this a tolerance rather than a
 * silent coercion, which is the distinction that matters:
 *
 *   1. NOTHING IS COERCED. A row either satisfies the schema or is not shown.
 *      There is no path here that renders a guessed or defaulted value — the
 *      failure mode this repo already refuses ("silently coercing is how you
 *      end up rendering `undefined` to a paying user", client.ts).
 *   2. THE DROP IS SURFACED, twice: `console.warn` at parse time — the thing
 *      the four outages above all lacked — and `droppedFixtures` on the
 *      response, which `FixtureListSection` renders as a visible line so a
 *      reader is told the list is short rather than shown a thinned page that
 *      looks complete.
 *   3. THE CONTRACT DOES NOT MOVE. `contract/fixturesPublic.schema.json` still
 *      says every row must satisfy `publicFixtureSchema`, so a server that
 *      emits a bad row still fails the cross-repo contract test in the API
 *      repo. Tolerance is what the CLIENT does about a violation, not
 *      permission for one.
 *
 * The alternative considered and rejected: keep failing the whole document,
 * on the argument that a half-list is a lie of omission. It is not the trade
 * being made — a document-level failure shows a reader NOTHING and tells them
 * nothing either, which is strictly worse than showing 67 of 68 and saying so.
 */
export const droppedFixtureSchema = z.object({
  /** The row's own `key` when it had a readable one — without it neither a
      log line nor a person can say WHICH fixture went missing. */
  key: z.string().nullable(),
  /** Zod's own issues, flattened to one line, so the console warning names the
      field that moved rather than just the count. */
  reason: z.string(),
});

export type DroppedFixture = z.infer<typeof droppedFixtureSchema>;

/**
 * Preprocessor: split a fixture-list body into the rows that parse and a
 * report of the ones that do not. Returns the body UNTOUCHED when nothing
 * fails (so `droppedFixtures` is absent, not an empty array, on the happy
 * path) and when the body is not a list-shaped object at all (so a genuinely
 * malformed document still produces the schema's own error rather than a
 * confusing empty list).
 */
function tolerateUnreadableRows(rowSchema: z.ZodType) {
  return (body: unknown): unknown => {
    if (!body || typeof body !== "object") return body;
    const rows = (body as { fixtures?: unknown }).fixtures;
    if (!Array.isArray(rows)) return body;

    const kept: unknown[] = [];
    const dropped: DroppedFixture[] = [];
    for (const row of rows) {
      const parsed = rowSchema.safeParse(row);
      if (parsed.success) {
        /* The RAW row is kept, not `parsed.data` — the outer schema parses the
           list again straight after this, and handing it pre-parsed output
           would mean defaults and transforms ran twice. */
        kept.push(row);
        continue;
      }
      const key = (row as { key?: unknown })?.key;
      dropped.push({
        key: typeof key === "string" ? key : null,
        reason: parsed.error.issues
          .map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`)
          .join("; "),
      });
    }

    if (dropped.length === 0) return body;

    /* The console line the four outages never had. Left in production builds
       on purpose: this is the only signal that a deployed page is quietly
       short of rows, and it costs one warning on a page that is already
       wrong. */
    console.warn(
      `[kev] dropped ${dropped.length} unreadable fixture row(s) of ${rows.length}`,
      dropped,
    );
    return { ...body, fixtures: kept, droppedFixtures: dropped };
  };
}

const freeFixturesBodySchema = z.object({
  generatedAt: z.string(),
  ratingsAsOf: z.string(),
  fixtures: z.array(freeFixtureSchema),
  /** CLIENT-SIDE, never on the wire — `.optional()` because the server does
      not send it and must not be made to. See the tolerance note above. */
  droppedFixtures: z.array(droppedFixtureSchema).optional(),
});

export const freeFixturesResponseSchema = z.preprocess(
  tolerateUnreadableRows(freeFixtureSchema),
  freeFixturesBodySchema,
);

/**
 * The ANONYMOUS file — `GET /teaser?artifact=fixtures-public`, PUBLIC, no
 * auth, no entitlement, no session. kev-docs/kev-landing-and-seo-plan.md
 * (2026-09-07, the newest of the two navigation docs and the one that
 * governs): a three-tier product, not two — this is the tier BELOW
 * `freeFixtureSchema` above, what a stranger and a search crawler see at `/`.
 *
 * Ned's line, load-bearing for this whole shape: "the existence of an edge is
 * public, its size is not." So `value` names WHETHER Kev has found value and
 * WHICH markets, never a probability, a fair price, a modal score or an xG —
 * `publicFixtureSchema` has no field for any of those to occupy, mirroring
 * `freeFixtureSchema`'s own "no market field" discipline one tier further
 * down. `handler_teaser.py`'s `PUBLIC_FORBIDDEN_KEYS` re-checks the same line
 * server-side, plus one check this schema cannot express: `read` may not
 * contain a single digit anywhere, which is why it is `z.string()` here and
 * not the `{text, generatedAt}` shape `fixturePageReadSchema` uses — a plain
 * string is what that handler-side digit scan actually walks.
 *
 * `value.found` is `boolean | null`, and the null is a real, distinct state —
 * "Kev hasn't called this fixture yet" — from `found: false` ("Kev has called
 * it and found nothing"). BUILD note: an absent verdict and a negative
 * verdict must never render the same, so `Home.tsx` treats `null` as "render
 * nothing" and `false` as its own quiet state, never silently merging the two.
 */
export const publicFixtureValueSchema = z.object({
  found: z.boolean().nullable(),
  markets: z.array(z.string()).default([]),
  /**
   * NULLABLE, and that is not tidiness — it is the unknown state.
   *
   * The exporter writes the whole verdict as `{found: null, markets: [],
   * count: null}` when it has no market feed to compare against, because a
   * count of `0` would read as "Kev looked and found nothing" when the truth
   * is that Kev has not looked yet. A non-nullable `count` here rejected every
   * row of the published file, so Zod failed the whole document and the
   * landing rendered "The fixture list is unavailable right now" over 52
   * perfectly good fixtures — with a 200 on the network tab and nothing in the
   * console. Shipped 2026-09-07; found by looking at the page.
   */
  count: z.number().int().nullable(),
});

export const publicFixtureSchema = z.object({
  key: z.string(),
  sport: sportSchema,
  competition: z.string(),
  kickoff: z.string(),
  home: z.string(),
  away: z.string(),
  /* NULLISH, not optional: the exporter writes `venue: null` when the lake has
     no venue for a fixture, and `.optional()` alone rejects an explicit null —
     the third field in this file to lose a whole page that way. */
  venue: z.string().nullish(),
  /** No numbers, anywhere — see this block's own note above. */
  read: z.string(),
  value: publicFixtureValueSchema,
  teamNews: fixtureTeamNewsSchema.optional(),
  /** Football's meeting history OR the NFL's per-club season records — see
      `fixtureLastFiveSchema`'s own note on why the two shapes stay distinct. */
  /* Caught: a form guide the page can't read costs the form guide, never the
     fixture (2026-09-22 — a new NBA shape hid all ten NBA games). */
  lastFive: fixtureLastFiveSchema.default([]).catch([]),
  /** Disclosure, not product — same reasoning `freeFixtureSchema.edgeRecord`
      carries, one tier further down: the settled record is a track record,
      not a priced call, so it stays visible with no account at all. */
  edgeRecord: fixtureEdgeRecordSchema.optional(),
  /** One line, per sport — "Kev's winning chances and fair odds need a free
      account", or the sport-specific equivalent the exporter writes. */
  signInPrompt: z.string(),
});

/**
 * `sports` is metadata, not the fixture list — `fixtures` below carries every
 * sport's rows in one array, keyed by each row's own `sport` field, and the
 * landing page groups by THAT rather than by walking this object. What shape
 * a no-fixtures-yet sport takes here (a flag, a note, absent entirely) is not
 * specified by kev-docs/kev-landing-and-seo-plan.md, so this is permissive on
 * purpose: the page never reads it to decide what to render, only `fixtures`
 * does — "render a sport with no fixtures as absent, not empty" is enforced
 * by grouping `fixtures`, not by trusting this map to say which sports exist.
 */
const fixturesPublicBodySchema = z.object({
  generatedAt: z.string(),
  sports: z.record(z.string(), z.unknown()).default({}),
  fixtures: z.array(publicFixtureSchema),
  /** CLIENT-SIDE, never on the wire — `.optional()` because the server does
      not send it and must not be made to. See the tolerance note above
      `freeFixturesResponseSchema`. */
  droppedFixtures: z.array(droppedFixtureSchema).optional(),
});

export const fixturesPublicResponseSchema = z.preprocess(
  tolerateUnreadableRows(publicFixtureSchema),
  fixturesPublicBodySchema,
);

/**
 * What the WIRE may carry, as opposed to what a parse produces.
 *
 * The mocks are typed with this rather than with `FixturesPublicResponse`, and
 * that is the whole point: the output type has every `.default()` filled in and
 * every `.optional()` resolved, so a mock typed as the OUTPUT cannot express a
 * body the exporter actually publishes — an absent `pushing`, an absent
 * `venue`, a `lastFive` that is neither. A mock that cannot be as ragged as
 * production is a mock that tests a shape nobody serves, which is how four
 * outages in one day all passed 1,289 green tests first.
 */
export type FixturesPublicWire = z.input<typeof fixturesPublicBodySchema>;

export type PublicFixtureValue = z.infer<typeof publicFixtureValueSchema>;
export type PublicFixture = z.infer<typeof publicFixtureSchema>;
export type FixturesPublicResponse = z.infer<typeof fixturesPublicResponseSchema>;

export type FixturePageFixture = z.infer<typeof fixturePageFixtureSchema>;
export type FixtureResultRow = z.infer<typeof fixtureResultRowSchema>;
export type FixtureResult = z.infer<typeof fixtureResultSchema>;
export type FixtureValueRow = z.infer<typeof fixtureValueRowSchema>;
export type FixtureValue = z.infer<typeof fixtureValueSchema>;
export type FixtureEdgeRecord = z.infer<typeof fixtureEdgeRecordSchema>;
export type FixtureMovementRow = z.infer<typeof fixtureMovementRowSchema>;
export type FixtureMovement = z.infer<typeof fixtureMovementSchema>;
export type FixturePageProp = z.infer<typeof fixturePagePropRowSchema>;
export type FixturePageProps = z.infer<typeof fixturePagePropsSchema>;
export type FixtureTeamNewsClub = z.infer<typeof fixtureTeamNewsClubSchema>;
export type FixtureTeamNews = z.infer<typeof fixtureTeamNewsSchema>;
export type FixtureLastFiveRow = z.infer<typeof fixtureLastFiveRowSchema>;
export type FixtureSeasonRecord = z.infer<typeof fixtureSeasonRecordSchema>;
export type FixtureSeasonRecords = z.infer<typeof fixtureSeasonRecordsSchema>;
export type FixtureLastFive = z.infer<typeof fixtureLastFiveSchema>;
export type FixturePlayerFormRow = z.infer<typeof fixturePlayerFormRowSchema>;
export type FixtureChip = z.infer<typeof fixtureChipSchema>;
export type FixtureMargin = z.infer<typeof fixtureMarginSchema>;
export type FixtureTotal = z.infer<typeof fixtureTotalSchema>;
export type FixtureTotalBand = z.infer<typeof fixtureTotalBandSchema>;
export type FixtureMultis = z.infer<typeof fixtureMultisSchema>;
export type FixtureSameGame = z.infer<typeof fixtureSameGameSchema>;
export type FixtureSameGameLeg = z.infer<typeof fixtureSameGameLegSchema>;
export type FixtureMultiSuggestion = z.infer<typeof fixtureMultiSuggestionSchema>;
export type FixtureMultiLeg = z.infer<typeof fixtureMultiLegSchema>;
export type FixtureMarketStatus = z.infer<typeof fixtureMarketStatusSchema>;
export type FixturePage = z.infer<typeof fixturePageSchema>;
export type FixturePagesBoardResponse = z.infer<typeof fixturePagesBoardResponseSchema>;
export type FreeFixtureResultRow = z.infer<typeof freeFixtureResultRowSchema>;
export type FreeFixtureResult = z.infer<typeof freeFixtureResultSchema>;
export type FreeFixturePropName = z.infer<typeof freeFixturePropNameSchema>;
export type FreeFixtureProps = z.infer<typeof freeFixturePropsSchema>;
export type FreeFixture = z.infer<typeof freeFixtureSchema>;
export type FreeFixturesResponse = z.infer<typeof freeFixturesResponseSchema>;

/**
 * Franco's saved squads — GET/PATCH/DELETE /fantasy/teams.
 *
 * ⚠️ `players` IS A LIST OF NAMES, NOT OF OBJECTS, and the UI must not wish it
 * otherwise. The store keeps thirty strings and nothing else: no position, no
 * club, no id. Everything a roster row shows beyond the name has to be fetched
 * or admitted as unknown, which is why the roster table renders a name on its
 * own rather than an empty cell where a position would go.
 *
 * ⚠️ `format` IS SOMETIMES THE EMPTY STRING, WHICH IS A STATE AND NOT A BLANK.
 * The server writes "" to mean "no scoring format was ever recorded for this
 * squad" — it is never null and never absent. A squad in that state cannot be
 * valued, because the same player is worth different things in points and in
 * 9-cat, so the page asks for the format rather than picking one.
 *
 * ⚠️ THE STORE CARRIES NO SPORT. Every row is `USER#{sub}` / `TEAM#{id}` with no
 * code on it, and only the NBA runner has ever written to it. That is why this
 * is fetched exclusively where `rules.has.savedTeams` is true: a football squad
 * shown from this endpoint would be an NBA roster under a Premier League
 * heading, and nothing in the payload would give it away.
 */
export const fantasyTeamSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** "" means no format recorded. See above — not a blank, a state. */
  format: z.string(),
  players: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
  revision: z.number(),
  /** The server holds one previous roster and can put it back. */
  canUndo: z.boolean(),
});

export const fantasyTeamsResponseSchema = z.object({
  teams: z.array(fantasyTeamSchema),
  /**
   * Server-owned ceilings, read rather than hardcoded. The page prints "3 of 5"
   * from this; a constant here would go stale the day the server changed and
   * would then be telling a user they had room they did not have.
   */
  limits: z.object({
    maxTeams: z.number(),
    maxPlayers: z.number(),
  }),
  /**
   * The scoring formats the STORE accepts, which is a different list from the
   * formats the page can explain (features/fantasy/rules.ts). They agree today.
   * Parsed as plain strings so the day they stop agreeing this fails on the
   * page's own lookup rather than at the boundary, where it would take the whole
   * squad list down over a fifth format nobody had described yet.
   */
  formats: z.array(z.string()),
});

export type FantasyTeam = z.infer<typeof fantasyTeamSchema>;
export type FantasyTeamsResponse = z.infer<typeof fantasyTeamsResponseSchema>;

/* ── the NFL player index ────────────────────────────────────────────────── */

/**
 * One published player. `nfl/player_index.py` owns the shape.
 *
 * ⚠️ EVERYTHING BUT `name`, `key` AND `season` IS NULLABLE, AND THAT IS THE FEED
 * RATHER THAN CAUTION. `pos`, `group` and `clubId` are written through a NaN-safe
 * `_text()` on the backend precisely because the provider omits them; a schema
 * that required a position would drop real players out of the search the day one
 * arrived without one.
 */
export const playerIndexRowSchema = z.object({
  name: z.string(),
  /** Already normalised BY THE BACKEND. The client never re-normalises this
      side — see features/fantasy/playerIndex.ts. */
  key: z.string(),
  pos: z.string().nullish(),
  group: z.string().nullish(),
  clubId: z.string().nullish(),
  season: z.number(),
});

/**
 * `GET /teaser?artifact=players-nfl` — public and unauthenticated, because the
 * file is names, positions and clubs from the licensed feed with no projection,
 * probability or price anywhere in it. See handler_teaser.py's own note.
 *
 * ⚠️ DELIBERATELY LOOSE ABOUT EVERYTHING EXCEPT `players` AND `normalisation`,
 * and this week is the reason. Five separate surfaces went out because a
 * single-document fetch met a schema stricter than its exporter — the gated
 * fixture page lost Value, Movement and Props over a missing `label`, with a 200,
 * a valid entitlement and nothing in the console. A single document has no
 * per-row tolerance to fall back on: it parses or the feature is gone.
 *
 * So this pins the two things the feature cannot work without and lets the rest
 * of a rich, still-evolving document through untouched. `counts`, `clubs`,
 * `limits`, `duplicateKeys` and the provenance fields are all published today and
 * none is read here; naming them would be a list to keep in step for no gain.
 */
/**
 * Per-row tolerance for the index, done as a PREPROCESS rather than a
 * `.transform()` on the array.
 *
 * Both drop unreadable rows. Only this one survives `npm run contract:export`:
 * `zod.toJSONSchema` refuses a transform outright ("Transforms cannot be
 * represented in JSON Schema"), so a transform here would have kept the schema
 * out of the exported contract — which is the artefact the backend is checked
 * against. `tolerateUnreadableRows` above takes the same shape for the fixture
 * lists, for the same reason.
 */
function tolerateUnreadablePlayers(body: unknown): unknown {
  if (!body || typeof body !== "object") return body;
  const rows = (body as { players?: unknown }).players;
  if (!Array.isArray(rows)) return body;

  const kept = rows.filter((row) => playerIndexRowSchema.safeParse(row).success);
  if (kept.length === rows.length) return body;

  /* The console line, kept in production builds on purpose: a squad form
     quietly short of players looks exactly like one that is complete. */
  console.warn(
    `[player index] dropped ${rows.length - kept.length} unreadable row(s) of ${rows.length}`,
  );
  return { ...(body as object), players: kept };
}

const playerIndexBodySchema = z.object({
  /** Bad rows are DROPPED, not fatal — the search is still useful with 3,177 of
      3,178 players in it, and useless if one malformed row empties it. */
  players: z.array(playerIndexRowSchema),
  /**
   * The rule this client must reproduce, plus the vectors that prove it does.
   *
   * Optional, and absent means "do not validate": `validationUsable` treats a
   * document it cannot verify exactly like one whose vectors fail. An index that
   * cannot be checked is not a licence to check names against it.
   */
  normalisation: z
    .object({
      version: z.union([z.string(), z.number()]).nullish(),
      vectors: z.array(z.object({ input: z.string(), key: z.string() })).default([]),
    })
    .optional(),
  /**
   * Club id → club name, all 32 of them.
   *
   * Read for the DEFENCE place, which is a TEAM rather than a person: `clubs` is
   * what makes "Ravens" resolvable to "Baltimore Ravens" without a second table
   * to keep in step with the feed. It was published all along and nothing looked
   * at it, which is why the Defence slot said there was nothing to look up.
   */
  clubs: z.record(z.string(), z.string()).optional(),
  season: z.number().nullish(),
  asOf: z.string().nullish(),
  source: z.string().nullish(),
});

export const playerIndexSchema = z.preprocess(
  tolerateUnreadablePlayers,
  playerIndexBodySchema,
);

export type PlayerIndexRow = z.infer<typeof playerIndexRowSchema>;
export type PlayerIndex = z.infer<typeof playerIndexBodySchema>;
